import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Search, FileText, Download, ArrowLeft } from 'lucide-react';
import { API } from '@/utils/constants';

/**
 * Help Page Component
 * Comprehensive in-app user guide with searchable documentation
 */
const HelpPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const navigate = useNavigate();

  // Comprehensive help content organized by sections
  const helpSections = [
    {
      id: 'whats-new',
      title: "What's New",
      icon: '✨',
      items: [
        {
          question: 'Recent updates (Feb 2026 — part 43: 📋 Copy button on Expenses & Purchases)',
          answer: `Recurring supplier bills (Alltel phone, fuel top-ups, insurance instalments, drive re-orders) meant re-typing the whole expense/purchase form from scratch every time. Now there's a Copy button next to Edit/Delete on every row.

Click 📋 Copy → the Add form opens pre-filled with:
- Supplier, Category, Description, Payment Method, GST %, Personal/Internal flags, Status, Notes (all copied)
- For Purchases: the full line-item list with stock refs, quantities and unit costs
- Date defaults to today (so most copies are 2 clicks + type-the-amount)

Blanked on purpose (forces conscious entry, avoids silent copy-paste errors):
- Amount (ex-GST + GST + Total)
- Invoice / Reference number
- Receipt attachment

A toast reminder appears — "Copying from {supplier} — please enter date & amount" — so it's clear you're creating a new record, not editing the original. The source row stays untouched.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 42: 🏢 Customers page now shows/edits Company Name + ABN)',
          answer: `Follow-up to yesterday's Company Name + ABN work — the Customers management page didn't expose those two fields, so you couldn't view or edit a customer's company details from the Customers list. Setting a company on a job would carry through to future bookings for the same mobile/email via autocomplete, but there was no way to fix it retroactively without editing every job.

Now, on the Customers page:
- Open any customer → the View pane shows Company Name and ABN rows (with "-" when blank so you can see the fields exist).
- Click Edit → Company Name and ABN inputs sit just below Name. Save.
- Next time you book a new job for that customer, picking them from the autocomplete auto-fills the company and ABN.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 41: 🏢 Company Name + ABN for business customers)',
          answer: `Booking a job for a business customer used to mean cramming the company name into the Customer Name field (which then mixed the individual and the business on every screen), and invoices only ever addressed the individual. Australian tax invoices to companies expect "Bill To: Acme Pty Ltd \\n Attn: John Smith" plus the ABN.

Now there are two optional fields sitting on the intake form's right column — Company Name (above Customer Reference) and ABN. Both are optional; leave them blank for personal-customer bookings.

Downstream effects:
- Invoice PDF + emailed invoice: when Company Name is set, the Bill To block now shows the company on top (bold), then "Attn: {Customer Name}", then contact/address, and a final ABN line when supplied. Blank company = old behaviour, no change.
- Customer autocomplete: picking an existing repeat customer pulls their most recent Company Name + ABN through automatically — no re-typing on every booking.
- Top-bar search: typing the company name (or part of it) finds the job.
- Job Card view: Company Name and ABN always show with a "-" fallback so you can see there's a field even when unused.
- Job Card edit: same fields exposed under Customer Information.

The stored Company/ABN on the customer record is only overwritten when a fresh job actually supplies one — a follow-up personal booking never blanks out previously-captured business details.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 40: ⚡ Fast Date Due editing — no more Edit → Save)',
          answer: `Setting a Date Due used to mean clicking Edit, changing the date, clicking Save — every single time. When a customer approves the quote overnight via the email link, that's the friction on every job every morning.

Two new fast paths, no Edit button needed:

1. Auto-prompt on approval — the moment you flip Status → Approved on the Job Card, a small dialog pops up asking "when is it due?" with quick-pick chips (Today, Tomorrow, +2 days, +1 week). Save writes the status change AND the Date Due in one go. Skip saves the status without a due date. No lingering "Save Changes" button afterwards.

2. Inline Date Due pill — the "Date Due:" line on the Job Card is now a clickable pill with a small pencil icon. Click it, pick a date, Save — done. Works regardless of status, so when the customer approved overnight via email and you see 3 jobs already sitting in Approved without a Date Due, it's now one click + Save per job.

The old Edit → Save flow still works if you prefer it. Backend unchanged — same PUT /api/jobs/{id}.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 39: 🐛 "Revenue by Status" table now shows real numbers)',
          answer: `Fixed: the "Revenue by Status" table on Reports → Financial was showing 0 jobs / $0 across every status, even when the Finished Revenue cards above it showed real numbers.

Root cause: the table filtered by client-side date_in (booking date), but for a cash-basis business with 17k+ Jim2-imported jobs whose date_in is historical but whose revenue_date (payment date) is recent, that filter returned essentially nothing. Meanwhile the summary cards used server-computed cash-basis figures — the two could never agree.

Now the table pulls a server-side cash_by_status breakdown filtered by revenue_date, so its rows total exactly to the Finished Revenue card above. For a data-recovery workflow only Finish / Not Recoverable / Not Proceeding statuses will normally hold cash-basis revenue — the other statuses correctly stay at 0.

New subtitle under the table heading makes the intent clear: "Cash basis — jobs whose payment landed in this period, grouped by their current status."`
        },
        {
          question: 'Recent updates (Feb 2026 — part 38: 📎 Emailed invoices now include the PDF as an attachment)',
          answer: `Previously clicking Email on an invoice sent the invoice inline in the email body only — no attached file. If you wanted to send a PDF to the customer, you had to click PDF, save it to Downloads, then manually attach it in Outlook or Gmail.

Now clicking Email attaches a freshly-generated Invoice_{job_number}.pdf alongside the same inline invoice body. Nothing about the dialog changes — same subject, same body, same CC/BCC, same template picker (invoice_email / paid_invoice_email). The customer gets both:
- The invoice rendered inline in the email body (as before), AND
- A proper PDF attachment they can save, forward, or file.

The PDF is regenerated in-memory at send time from the current job data, so the attachment is always fresh — no stale disk copies.

The PDF button itself is unchanged — it still downloads to your Downloads folder, for the times you want the file locally without sending an email.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 37: 🖨️ Sticker window auto-closes after printing)',
          answer: `Print Sticker used to leave the small preview tab open after you clicked Print (or Cancel), which meant one extra click every time to tidy up.

The popup now closes itself as soon as the print dialog is dismissed — whether you actually printed or hit Cancel. Works across Chrome, Edge and Firefox on Windows and macOS.

A 60-second safety timeout ensures the popup never lingers even on odd browser/OS combos where the underlying event doesn't fire.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 36: 🧾 Invoice PDF now includes the service/labour line)',
          answer: `Fixed: emailed and downloaded invoice PDFs were rendering stock lines and generic additional items correctly, but silently omitting the primary service/labour line (Data Recovery, Emergency Service, etc.), so totals looked "off" to customers.

The PDF builder now emits the service line as the first row of the invoice table, exactly matching the on-screen preview and the totals block.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 35: 🛡️ Startup backup OOM crash-loop fixed)',
          answer: `Fixed: after some redeploys the site would come back up for a few seconds then crash-loop for ~10 minutes before stabilising.

Root cause was the daily backup routine kicking off at boot and loading every MongoDB collection into RAM before zipping — pushing the 200 MB pod past its limit and OOM-killing the process.

Startup backup is now (a) delayed by 5 minutes so the pod stabilises first, (b) streamed collection-by-collection into a temp file on disk instead of RAM, and (c) skipped entirely if a recent OOM crash-loop is detected. Backups still run on their normal schedule.`
        },
        {
          question: 'Recent updates (Aug 2026 — part 34: 💡 Expense Description typeahead)',
          answer: `The Add Expense form's Description field is now a single typeahead — same style as the Category field. Start typing and suggestions appear underneath, sorted by:

- **Recent** (purple pill) — the last 20 descriptions you've actually typed on real expenses, so the app learns your patterns (e.g. "Optus mobile", "Voipline landline")
- **Common** — a built-in list of typical business expense descriptions

Free-text is never blocked. If none of the suggestions match, just keep typing — your text is saved as-is. Press Escape or click anywhere else to dismiss the suggestions.

The old "Common..." dropdown next to the description field is gone — everything now lives in one field.`
        },
        {
          question: 'Recent updates (Aug 2026 — part 33: 🐛 Invoice email link fix)',
          answer: `Fixed a bug where emailed invoice PDF links would return "Not Found" after a redeploy or pod restart. The PDFs used to be written to ephemeral pod disk which gets wiped on restart. Now the app regenerates the PDF on demand from the stored job data if the file is missing — every historical invoice link is permanently valid.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 33: 🏢 Expenses By Supplier report)',
          answer: `A new Reports tab that answers "how much am I paying Telstra / Google / Aus Post / Emergent / Red Energy this month?" — same layout as Supplier Spending, but for non-stock expenses.

WHERE
Reports → 🏢 **Expenses By Supplier** (between Supplier Spending and Job Details).

WHAT IT SHOWS
- **Summary cards**: Active Suppliers · Total Spent · Total Expenses · Top Supplier ($).
- **Ranked table**: Rank · Supplier · # of expenses · Total Spent · Avg Expense · Last Expense date. Sorted by Total Spent descending.
- Uses the standard date range picker so you can view All-Time / This Month / YTD / etc.

WHY IT EXISTS ALONGSIDE SUPPLIER SPENDING
Supplier Spending only pulls from Purchases (stock buys). Expenses By Supplier pulls from Expenses (services, subscriptions, utilities, freight, etc.). Same supplier can appear in both — e.g. **Officeworks** shows up on both if you buy both stationery-as-expense and stock media there.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 32: 🐛 Address autocomplete unit-number fix)',
          answer: `Small but annoying fix: when you picked an address like "23/4 Googlet Street" from the autocomplete list, the "23/" prefix was being dropped, leaving just "4 Googlet Street" in the field. Root cause was the backend parser missing Google's "subpremise" component (their name for unit/apartment number). Now handled — the field lands with the full "23/4 Googlet Street" as expected on first click.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 31: 📦 Stock allocation visibility — "Where is my stock?")',
          answer: `Stock control feature — see which jobs are currently holding each stock item, so you can trace missing inventory or check where in-flight drives are sitting.

WHERE
Stock page — every row now has a small ▶ chevron at the start and a new **"On Jobs"** column.

WHAT THE "ON JOBS" COLUMN SHOWS
- Number of units currently allocated to unpaid jobs (e.g. **8 (8 jobs)**), in purple.
- **—** if none.
- **"Allocated" = the customer hasn't paid yet.** A drive stays on this list from the moment you add it to a job's invoice all the way through Approved / Finish / awaiting-collection — the physical drive is still in your office regardless of workflow status. Only when you apply payment (payment_status → Paid) does the drive drop off the allocated list, because that's when the customer has actually collected and left with it.

CLICK TO DRILL IN
- Click the ▶ chevron (or the "On Jobs" number) → inline expand shows the jobs holding this stock:
  · Job # (clickable — jumps straight to the job)
  · Customer
  · Status (colour-coded)
  · Date In
  · Qty allocated
- Click ▼ chevron again to collapse.

HISTORY / ACTIVE TOGGLE
- Inside the expand, a pill button **"Show history (all-time)"** switches to include paid jobs too. Useful for tracing "which customer paid and took drive X 3 months ago?".
- Historical (paid) rows appear slightly dimmed to visually distinguish them from live allocations.

WORKFLOW MATCH
Stock is deducted from the shelf the moment you add a line to a job's invoice — even if the job is still in-progress. Deleting the line puts the stock back on the shelf automatically. The drive stays in the "On Jobs" count through the whole lifecycle (Approved → Finish → awaiting-collection) and only leaves when the invoice is Paid.

USE CASES
- **Finding missing stock**: On Jobs says 2, current qty says 4, but you thought you had 8 → 2 unaccounted for. Cross-check with the Stock Reconciliation report.
- **"Where's that Samsung I ordered?"**: expand the row → see it's on Job 22850 Alan Smith.
- **Investigating stock leaks**: toggle history → see the last N terminal jobs that consumed this SKU.

NOTE
This works alongside the existing Stock Reconciliation report on Reports → 🧾 Stock Reconciliation. Reconciliation gives you the aggregate maths across a date range; this new drill gives you a per-item "who has it right now" view.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 30: 📊 Reports made fully clickable + small polish)',
          answer: `Today was about making reports drillable end-to-end and tidying a few small things.

📊 EXPENSE REPORT — EVERY CHART IS NOW CLICKABLE
- **Expenses by Category donut** → click any slice (or its legend text) to filter the Expense Details table below to just that category. A filter chip appears above the table with an × to clear.
- **Expenses by Payment Method donut** → same behaviour for payment methods.
- **Monthly Expense Trends bar chart** → click any month's bar to filter the table to that month. Selected bar darkens so you can see which is active. Click again to clear.
- **The two filter types combine** (AND) — click "Freight - Postage" slice + click "2026-07" bar → shows only Freight expenses in July. Each chip clears independently.
- **"Other" bucket** in donuts groups categories under 3% — click it and the table filters to every small category inside (tooltip shows the full list).
- **View button on every row** → jumps straight to that expense on the Expenses page, scrolls to it, and glows it amber for 3 seconds so you can spot it.

💬 REPORT CARD DESCRIPTIONS
Every card in **Job Distribution / By Location / Priority / Supplier Spending / Job Details / Expense Report** now has a one-line description under its title explaining what's counted and which date field drives it. Prevents "why doesn't this number match X" confusion at the source.

📌 PAYMENT OVERVIEW TOOLTIPS
Hover **Total Revenue / Amount Collected / Outstanding Balance / Collection Rate** on the Financial tab — each shows an explanation of the exact formula. Includes the deposit-timing gap (why Total Revenue can differ from Amount Collected in a period).

🐛 JOB DISTRIBUTION DRILL-IN FIXED
The **Not Recoverable** row card showed 6 jobs / $1,774 but expanding it only showed 1 job / $660. That's fixed — drill-in now uses the same cash-basis grain as the card, so counts and revenue match 1:1. Same fix applied to **By Location** and **Priority** tabs.

🥯 EXPENSE PIE CHART LABEL OVERLAP FIXED
The old pie charts piled labels on top of each other at the bottom when there were 15+ categories. Now they're **donuts with a right-side legend** (Name (%)), no on-chart labels, and slices under 3% grouped into "Other". Clean, readable, still shows exact $ on hover.

🔧 SMALL FIXES
- **Job Type Analysis** — clicking the Job # in the drill-in now correctly opens the job (was navigating to the dashboard).
- **⭐ Review button** shortened from "Send Review Request" to just "⭐ Review" so it fits alongside the other JobDetail buttons on paid invoices. Hover shows the full description.
- **Comments History** on JobDetail now shows **newest first** (was oldest first).
- **Stock page** items now sorted **A→Z by item name** (case-insensitive).
- **Suppliers page** now sorted **A→Z by name** (case-insensitive).`
        },
        {
          question: 'Recent updates (Feb 2026 — part 29: 🔬 Job Type Analysis report — recovery rates per media type)',
          answer: `A new report tab that answers "how are we going on 3.5" HDDs this quarter vs SSDs?" — recovery outcome per job type, filterable by date range like every other report.

WHERE
Reports → 🔬 Job Type Analysis tab (last row of tabs).

WHAT IT SHOWS
- One row per Job Type (3.5" HDD, 2.5" HDD, SSD, RAID, USB, SD, etc. — sourced from Settings → Job Types).
- Columns: Total · Finish (count + %) · Not Recoverable (count + %) · Not Proceeding (count + %) · Success %
- Total row at the bottom summing every type.
- Sorted by Total descending — busiest media on top.

WHICH JOBS ARE COUNTED
Only jobs that reached a **final** status (Finish / Not Recoverable / Not Proceeding) with a **date_finished inside the selected date range**. Jobs still in progress (Booked / Quoted / Wait / Approved / Parts) are excluded on purpose — their outcome isn't decided yet and would pollute the % columns.

The date_finished field is auto-stamped when you set a job to any final status, so you don't have to enter it manually.

DRILL-IN
Click any row → inline table shows the individual jobs (Job # · Customer · Status · Date In · Date Finished). Click the Job # to jump straight to that job. Click the row again to collapse.

EXPORT
"Export CSV" button ships the currently-filtered breakdown (including totals row) to a spreadsheet. File name includes the date range.

REVENUE COLUMN
The rightmost column shows **cash-basis revenue** per type — sum of revenue_amount on those jobs (i.e. only counts money actually received, matching P&L / BAS / Dashboard Revenue). Not Recoverable and Not Proceeding jobs usually contribute $0, so the number roughly tracks the Finish column but tells you _which media type is making you the money_.

LEGACY JOB TYPES IN THE LIST
You may see legacy strings like **"AC install"**, "Sales", "iPhone Recovery", "HDD Data Recovery", "Floppy Recovery" — these are historical job_type values imported from Jim2 that don't match your current Settings → Job Types dropdown. The report is defensive and shows every distinct value present in the data, so nothing gets hidden. If you want, you can ask me later to build a bulk-rename utility to fold these into your current types.

USE CASES
- Quoting confidence: "our 3.5" success rate is 68% this quarter — factor that into the recovery-not-possible refund policy."
- Spotting trends: if SSD Not Recoverable % is climbing month-over-month, that's a signal.
- Justifying pricing or refusing risky media types when the historical numbers say so.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 28: 💳 Default payment method for Expenses & Purchases)',
          answer: `You almost always pay by American Express, and were re-selecting it on every Expense and Purchase. Now you can set it once.

WHAT'S NEW
- Go to **Settings → Dropdown Values → Payment Methods**.
- If "American Express" isn't in the list yet, click **Add New**, type it in, hit Add.
- Below the list you'll see a new **"Default Payment Method"** dropdown — pick American Express (or any other method) and it saves immediately.

WHERE IT APPLIES
- New **Add Expense** form → Payment Method is pre-filled with your default.
- New **Record Purchase** form → same.
- **Quick Entry chips** — those still use whatever payment method the chip's template has; only if the template leaves it blank does it fall back to your default.
- **Editing an existing** Expense or Purchase keeps the record's original payment method (no surprise overwrites).

DEFAULT BEHAVIOUR IF LEFT BLANK
- Pick **"No default (use Bank Transfer)"** to restore the previous behaviour — new forms open with Bank Transfer selected.

You can override the payment method on any individual entry — this is just about not having to click the same dropdown every time.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 27: 🗄️ Durable receipt storage — receipts survive redeploys)',
          answer: `You had a receipt disappear on production after a redeploy — the fix is now live in preview.

WHAT WAS BROKEN
Expense receipts (and any other file upload) were being saved to the pod's local disk, which is ephemeral. Every production redeploy wiped the disk. The database still remembered the URL, so clicking "View" showed a 404.

WHAT'S CHANGED
- All new receipt uploads now go into **Emergent Object Storage** — durable cloud storage that survives redeploys forever.
- The **View** link now goes through a backend-served download endpoint (auth-checked). Same one-click behaviour, but the file's actually there next time.
- Deleting a receipt from an expense still works the same — it clears the reference on the expense.

HISTORICAL RECEIPTS
- The receipt(s) you uploaded before this change are gone from production disk. Nothing to recover — just re-upload from your local copy.
- New uploads from this point onwards are permanent.

NO ACTION REQUIRED after redeploy — same View / Change / Delete buttons, just working properly now.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 26: ⚡ Dashboard speed-up)',
          answer: `Two changes make the Dashboard noticeably snappier — especially when you jump from job → dashboard → next job all day.

⚡ WHAT CHANGED
1. **Backend list response is 38% smaller.** The /api/jobs endpoint no longer returns heavy fields you never see on the Dashboard (invoice email history, activity log, quote history, stock items, line items, comments, etc.). Those are still fully available on the job detail page.
2. **Dashboard now caches the jobs list for 30 seconds.** After the first load, jumping into a job and returning to the Dashboard shows the list instantly from the cache — no visible refetch. A silent background refresh keeps the data fresh. Any mutation (closing a job, ticking Contacted, etc.) invalidates the cache so you always see the latest state.

⚡ WHY IT MATTERS
- Response size 3.47 MB → 2.14 MB for 1000 jobs.
- Response time ~0.47 s → ~0.37 s (fresh fetch).
- Return-to-dashboard time ~0.4 s → **near-instant** (cache hit).

⚡ NO ACTION REQUIRED
The change is transparent — no new buttons, no new settings. Just faster.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 25: 📥 Bulk expense entry — enter multiple transactions in one form)',
          answer: `For repeating charges (Google Ads, owner drawings, etc.) you can now enter every transaction in one submit instead of filling the form N times.

📥 HOW TO USE
1. Expenses → **Add Expense** as normal.
2. Fill in the shared fields once: Description ("Google Ads"), Category ("Advertising & Marketing"), Supplier ("Google"), Payment Method ("Amex"), etc.
3. Fill in the FIRST transaction's Date + Amount at the top (this is Line 1).
4. Scroll to **Additional transaction lines** and click **➕ Add another line**. A new row appears with its own Date + Amount (ex GST) + GST (10%) + Total, exactly like the main fields — including the **GST-Free** checkbox for GST-free lines.
5. Repeat for every extra transaction. Each row has an **✕** remove button.
6. The green chip at the bottom shows how many transactions you're about to create and the combined total.
7. Hit **Add Expense** — the app creates N separate expense records, one per line, on their individual dates. They appear in the expense list and in P&L exactly as if you'd entered each one by hand.

WHEN NOT TO USE IT
- If each transaction has a different supplier / category / description, enter them separately (the multi-line mode shares those fields).
- Editing is still one row at a time (as before) — the multi-line UI only appears on Add.
- Receipt uploads: attach a receipt for a bulk submit at your own preference; the same receipt WON'T be split across rows.

WHY
Zero extra UI to learn, saves 20 clicks a month for high-frequency charges like Google Ads. Each entry is still fully independent — you can edit or delete them individually later.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 24: Paid Tax Invoice email + ⭐ Google Review Request workflow)',
          answer: `Two related email-template features that make paid customers easier to close out.

📧 PAID TAX INVOICE EMAIL (NEW Feb 2026)
When you click **📧 Email** on a Paid job the dialog now shows a **Template** dropdown pre-selected to **Paid Tax Invoice**:
- Default body: short thank-you note instead of the standard payment-instructions body.
- Override any time — flip the dropdown to **Invoice (payment instructions)** if you want the standard version.
- Both templates are editable under **Settings → Email Templates**. Placeholders: {customer_name}, {job_number}, {business_name}, {business_phone}, {business_email}.

⭐ GOOGLE REVIEW REQUEST (NEW Feb 2026)
Send a review request to happy customers a few days after their invoice was emailed.

- **One-time setup**: paste your Google review URL under **Settings → Email Templates → ⭐ Google Review Request Email → Google Review URL**. Grab the link from your Google Business Profile → "Ask for reviews".
- **Template**: subject and body are editable in the same section. Uses a **{review_link}** placeholder in the body.

Two ways to send:
1. **From the Job Detail card** — for Paid jobs whose invoice has been emailed, an **⭐ Send Review Request** button appears next to the Email button. Click it → preview the rendered email → **Send**.
2. **From Reports → ⭐ Review Requests** — a dedicated call-list showing every paid customer whose invoice was emailed at least N days ago (default 7, adjustable 0–30) and who hasn't been asked yet. Click **⭐ Send** on each row → preview → **Send**. Row disappears once done.

Once sent:
- A **⭐ Review requested** badge replaces the button on the Job Detail (hover for who + when).
- The customer disappears from the Reports call-list — no double-asking.
- An entry is added to the job's activity log.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 23: "📞 Contacted" dashboard toggle)',
          answer: `Turn any dashboard filter into a live call-list — one tick per customer as you work through them.

📞 CONTACTED PILL (NEW Feb 2026)
Every row on the Dashboard now has a small **📞 Contact** button after Print. Click it:
- Grey outline → green filled **📞 Contacted** — the row is now visually flagged as "I've rung this one".
- Hover the green pill to see who ticked it, when, and which status the tick was made against (e.g. "Contacted 2h ago by admin — status was Quoted").
- Click again to un-tick.

Auto-reset on status change:
- When a job moves to a new status (Quoted → Approved, Approved → Finish, etc.) the tick automatically **clears**, because "contacted about being Quoted" is meaningless once the job is Approved. Each stage starts with a clean slate.

Filter to just uncontacted rows:
- New chip **📞 Not yet contacted** next to the payment chips at the top. Click it and the table shortlists just the active jobs you haven't rung yet — turn your dashboard into a working call-list, tick as you go, and rows disappear once ticked.

Audit trail:
- Every tick and un-tick is recorded on the job's activity log ("Marked as contacted (by admin) — status was 'Quoted'") so you can look back over the history if needed.

Where it shows:
- All statuses (Booked, Quoted, Wait, Approved, Parts, Finish, etc.) — the tick auto-resets on status change so terminal statuses will rarely have it set anyway.
- Dashboard only for now. If you'd like it on the job detail page too, just say the word.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 22: Stock Reconciliation + JWT session aligned to auto-logout)',
          answer: `Two behind-the-scenes fixes that solve real complaints.

⚖️ STOCK RECONCILIATION (NEW Feb 2026)
Reports → **⚖️ Stock Reconciliation**. For every stock item the app now shows:

    Purchased   Allocated   Expected   Current   Δ
       2           2           0          2      +2  ← drift

**Expected** = (all-time purchases) − (all-time job allocations).
**Current** = the number the app currently displays for this item.
When they disagree the ledger has drifted (usually a stock allocation was reversed at some point without a matching physical action).

- The banner at the top summarises how many items have drift.
- Rows with drift are highlighted red.
- Click a row to expand — you'll see every contributing purchase (date, supplier, invoice, qty) and every contributing job (#, customer, status, paid date, qty). Click a job to jump to it.
- Hit **Reconcile** on a drifted row to set Current = Expected. Every reconcile is logged to the stock item's activity so you have an audit trail.
- Tick **Show clean items too** to see every stock item, not just the drifted ones.

🔒 SESSION LIFETIME NOW MATCHES AUTO-LOGOUT TIME
Previously your login token had a fixed 8-hour lifetime. If you logged in at 8:45 AM, you'd be booted with a "Session expired" toast at 4:45 PM even though Auto-Logout was set to 7:00 PM. Fixed: the token now expires exactly at the configured Auto-Logout Time (plus 5 minutes grace so the pre-logout warning has time to appear). One source of truth — change Settings → Auto-Logout Time and it takes effect on the very next login.

🐛 STOCK REMOVE BUG FIX (SILENT)
When the same stock item appeared on a job twice (e.g. added, wrong price, added again with correct price), clicking Remove on ONE row was silently deleting BOTH rows from the job and only refunding one to the master ledger. This has been fixed — each row now has its own unique id and Remove only affects the row you clicked. A one-time backfill assigns ids to all existing rows on backend startup.`
        },
        {
          question: 'Recent updates (Feb 2026 — part 21: P&L card clarity — Personal-use drill-in + "included in Net Profit" tips)',
          answer: `Small polish so the P&L page is easier to read at a glance.

💡 P&L INFO TIPS + PERSONAL-USE DRILL-IN (NEW Feb 2026)
Reports → Profit & Loss now shows a subtle italic hint on the "⚡ Emergency Service Revenue" and "➕ Additional Line Items Revenue" cards:

    ℹ️ Already included in Net Profit above — shown separately as an informational breakdown.

Those cards are just breakdowns of what's already inside the top Net Profit figure — they are **not** additional revenue to add on top. The hint makes that explicit.

The "🏠 Personal-use items you claimed" card is now **clickable** the same way the Emergency Service card is. Click it to expand a table showing each individual contributing item:

    Date        Description       Category      Supplier   Ex-GST  GST    Total
    07/07/2026  Coffee for site   9999 – Other  Leader     $332.00 $33.20 $365.20
    …

A complementary tip on that card explains it works the opposite way — those items were **already deducted** from Net Profit (as expenses/stock purchases), and the red add-back number shows what profit would have been without them ("true business profit").

Nothing else changes. Zero new tabs, zero new settings.`
        },
        {
          question: 'Recent updates (July 2026 — part 19: 📦 Stock Analysis report — per-item profit)',
          answer: `New Reports tab: see exactly which stock items are making you money.

📦 STOCK ANALYSIS TAB (NEW Jul 22)
Reports → 📦 Stock Analysis. For every paid job in the selected period, the app aggregates every stock line into a per-item table:

    Brand + Item              Qty Sold  Revenue  Cost   Profit  Margin
    Toshiba 1TB Hard Drive       10    $1,240   $1,091 $149    12.0%
    Seagate 2TB Hard Drive       …
    (Totals row at the bottom sums everything)

Features:
• **Cash-basis** — matches every other financial figure in the app. A stock line counts once the customer has paid (job.revenue_date in the period), not when you allocated it.
• **Date range** — uses the same period selector as the rest of Reports (All Time / This Month / Last Month / This Quarter / Year to Date / Last 7/30 Days / Custom).
• **Sortable** — click any column heading to sort. Default is Profit descending (top earners first). Click again to flip.
• **Search box** — filter the table live by brand or item name ("toshiba", "1TB", etc.).
• **Drill-in** — click any row to expand the contributing job lines: Job #, Customer, Paid date, Qty, Sell $, Cost $, Line Sell $, Line Profit $. Click a job in the drill-in to jump to that job.
• **Colour cues** — profit shown green if positive, red if negative. Margin green ≥ 20%, amber ≥ 10%, red below.
• **CSV export** — dump the (filtered) rows as CSV for Excel review.

Use it to answer: "Which brands make me the best margins?" · "How many Toshiba drives did I sell this quarter?" · "Which items lose me money?" (rows with red profit).`
        },
        {
          question: 'Recent updates (July 2026 — part 18: Filter presets now sync across devices)',
          answer: `Your presets follow you everywhere — phone, laptop, workshop PC — instead of being tied to one browser.

☁️ SERVER-SIDE PRESET SYNC (NEW Jul 22)
The Filter Presets you save from the Dashboard's ⭐ Presets button are now stored server-side per user account. Log in from any device — desktop, iPad, phone — and every preset you've saved is right there waiting.

How it works under the hood:
• Save a preset → written to your user record on the server AND cached in this browser (localStorage) for instant re-open.
• Delete a preset → same, both places.
• Open the dashboard on a new device → the app fetches the server-side list on mount. If your new device already had local presets from before this update, they're automatically pushed up to the server the first time (nothing gets lost during the migration).
• If the server is briefly unreachable, the localStorage cache keeps the popover populated with your last-known list. Any changes made offline sync back up next time the server responds.

Nothing to configure — just save presets the same way as before and they'll now show up on every device you're logged into.`
        },
        {
          question: 'Recent updates (July 2026 — part 17: Filter presets — save named filter combos)',
          answer: `One click to jump into your favourite views — no more re-clicking the same filters every morning.

⭐ FILTER PRESETS (NEW Jul 22)
Right next to the Search / Clear All buttons on the Dashboard there's now a **⭐ Presets** button. Click it to open a small popover:
• **Top** — a live summary of the currently-active filters ("Finish · Un-paid awaiting · 💵 Deposit"), a name input, and a Save button. Type a name, hit Save → you have a saved preset.
• **Bottom** — the list of your saved presets. Click any name to apply it (the URL updates and the whole dashboard re-filters instantly). Click the × on a row to delete it.

Presets are saved per-browser (localStorage). They persist across page refreshes and Emergent redeployments. Each one captures your current combination of Status card, Payment status, Progress status, Chip filter and Search term.

Ideas for presets:
• "Chasing payment" — Status: Finish + Chip: Un-paid awaiting
• "Ready to hand back" — Chip: 💰 Paid – not collected
• "Rush jobs today" — Progress: WIP + something else
• "Approved this week" — Status: Approved + Search: <this-week>
• "SSD queue" — Search: SSD (or use with Progress: Not Started)

Nothing changes for existing users — the button just appears. Ignore it if you don't need presets.`
        },
        {
          question: 'Recent updates (July 2026 — part 16: Dashboard filters now persist in the URL)',
          answer: `No more "click Approved → open job → back → filter gone" cycle.

🔗 URL-PERSISTED FILTERS (NEW Jul 22)
Every filter you set on the Dashboard is now written to the URL as you click:
• Status card (Approved / Booked / Finish / etc.) → \`?status=Approved\`
• Payment status (Awaiting) → \`?payment_status=Awaiting\`
• Progress status (Imaging / WIP / etc.) → \`?progress=Imaging\`
• Payment-flag chip (💵 Deposit / 💰 Paid / etc.) → \`?chip=deposit\`
• Search box → \`?q=your-search\`

What this fixes:
• Click Approved → open a job → hit Back in the browser → you're back on the Approved list, not the full list.
• Deep-link a filter view — bookmark, share via email, pin to home screen. "Give me the URL for my Un-paid awaiting list" is now a thing.
• Refresh the page → filters stay put.
• Combine multiple filters and every click stacks in the URL — Approved + Deposit + Search "smith" reads back exactly like that when you return.

Nothing changes visually — same status cards, same chip row, same search box. It's just that the app now remembers what you were doing.`
        },
        {
          question: 'Recent updates (July 2026 — part 15: Additional Line Items on invoices)',
          answer: `Add ANY row to your invoice, not just labour + stock + emergency.

➕ ADDITIONAL LINE ITEMS (NEW Jul 22)
On the Job Detail page → Work & Billing panel, right below Stock Items, there's now an "➕ Additional Line Items" section with an "+ Add line item" button. Each line has:
• **Description** — free text (e.g. "Computer Repair", "2nd hand RAM 8GB DDR3", "Weekend Callout")
• **Category** — Labour · Part · Other (drives report grouping)
• **Qty**, **Unit $ (inc GST)**, **Total** (auto)
• 🗑️ delete button

Add as many as you like. Save (or Generate Invoice) — the Summary auto-tallies and the invoice PDF/preview renders each entry as its own row between Stock Items and Emergency Service:

    SERVICES ......................... $580.00
    1TB HDD Brand X (qty 1) .......... $130.00
    Computer Repair (qty 1) .......... $300.00   ← NEW line item
    2nd hand RAM 8GB DDR3 (qty 1) .... $ 60.00   ← NEW line item
    Emergency Service ................ $250.00
                        Subtotal / GST / Total  $1,320.00

Use cases:
• **Mixed jobs**: Computer Repair on a Data Recovery ticket — add "Computer Repair $300" instead of muddying the recovery labour figure.
• **Non-stock parts**: "2nd hand RAM 8GB DDR3 $60" without polluting your Stock inventory.
• **One-off services**: Tune-up, virus removal, callout fees, donations — anything you'd otherwise squeeze into Labour.

📊 P&L REPORTING BY CATEGORY (NEW Jul 22)
Reports → Profit & Loss now has a teal "➕ Additional Line Items Revenue" card showing the total inc-GST across all line items in the selected period. Click any of the Labour / Part / Other rows to drill in and see every contributing job (Job #, Customer, Description, Paid date, Amount). Clicking a row jumps to that job. Same cash-basis rule as everything else on the Financial tab.

Nothing changes on jobs with no additional line items — the invoice + summary render exactly like before.`
        },
        {
          question: 'Recent updates (July 2026 — part 14: Payment-flag chip filter on Dashboard)',
          answer: `One-click filter to isolate the money-in / money-owed slice of your working list.

🎯 CHIP FILTER ROW (NEW Jul 21)
Directly above the main Dashboard jobs table, a row of chips lets you filter the table to a specific slice, without touching search or the status cards. Each chip shows a live count of how many jobs would match:
• **All 12** — everything (default)
• **💵 Deposit paid 3** — jobs where you've ticked "Mark Deposit Received"
• **💰 Paid 4** — jobs where you've ticked "Mark as Payment Received"
• **💰 Paid – not collected 2** — customer paid AND device still on your shelf (your "call to collect" queue)
• **Un-paid awaiting 5** — customer hasn't paid AND job is still active (your "chase for payment" queue)

Click a chip → the table below filters instantly, chip turns solid coloured, and a "Showing X of Y" indicator appears on the right. Click "All" or click the same chip again to reset. Composes cleanly on top of the existing search box, status card filter, payment-status dropdown, and progress status filter.

If your filter yields no rows, a small "No jobs match this filter — Clear chip" prompt appears in the table area so you're never confused about why the list looks empty.

Everyday flow:
1. Open Dashboard.
2. Click **💰 Paid – not collected** → see who owes you nothing but hasn't picked up.
3. Ring/SMS them from the row.
4. Click **Un-paid awaiting** → see who owes you money.
5. Send collection notices.
6. Click **All** → back to normal view.

Zero scrolling, zero cross-referencing.`
        },
        {
          question: 'Recent updates (July 2026 — part 13: Deposit / Paid pills on Dashboard job rows)',
          answer: `See at a glance which shelved jobs are money-in-the-bank vs still-owing.

💵 💰 AT-A-GLANCE MONEY-IN PILLS (NEW Jul 21)
On the Dashboard's main jobs table, small colour-coded pills now appear next to the customer name whenever a job has been flagged as deposit received or payment received:
• **💵 Deposit** — blue pill, means you clicked "Mark Deposit Received" on the Quote card
• **💰 Paid** — green pill, means you clicked "Mark as Payment Received" on the Work & Billing header

Rows with neither flag stay perfectly clean — no visual noise. Rows with both flags show both pills side-by-side (great for those "customer paid deposit + balance, hasn't collected yet" scenarios).

Hover any pill to see the exact timestamp + who marked it (matches the button tooltips in Job Detail). Click the row → jumps to the job so you can flip the flags off once the device is collected.

Why: previously you'd know a job was paid-but-not-collected only by opening the Comments panel or the Job Detail page. This surfaces it at 2-second-scan speed on your main working screen.`
        },
        {
          question: 'Recent updates (July 2026 — part 12: Mark as Payment Received button)',
          answer: `A visual companion to the existing "Mark Deposit Received" button — for the "customer paid but hasn't collected yet" scenario.

💰 MARK AS PAYMENT RECEIVED (NEW Jul 21)
On any job, the Work & Billing section now has a "💰 Mark as Payment Received" green dashed pill sitting to the right of the section title. One click:
• Timestamped comment auto-added to the timeline ("💰 Payment received (marked by <you>)")
• Green dashed pill flips to a solid pill: "💰 Payment Received · 21 July" with a hover tooltip showing the full date-time + who marked it
• Click the solid pill again to reverse — reversal is also logged (audit trail intact)

No payment record is created — this is a pure visual scheduling flag. The formal Payments section is still the source of truth for the invoice/GST/P&L side of things. Use this button when the customer has paid (e.g. bank transfer confirmed) but their device is still sitting on your workshop shelf, so you can see the status at a glance without scrolling.

Mirrors the existing Deposit Received workflow 1:1 — same UX, same audit-trail behaviour, same "1 click to mark, 1 click to reverse" ease.`
        },
        {
          question: 'Recent updates (July 2026 — part 11: Sydney timezone fix on all form date defaults)',
          answer: `Fixed a subtle but annoying timezone bug: date fields on forms were showing YESTERDAY's date to Sydney users before 10 AM local time.

🐛 THE BUG
At 09:42 AM Sydney AEST (21 Jul), opening the Add Expense dialog pre-filled the Date field with 20/07/2026. Same problem on Add Payment, Add Purchase, New Job's Date In default, and a couple of report toasts. Under the hood, the frontend was calling \`.toISOString()\` which returns UTC — and Sydney is UTC+10, so anything before 10 AM local time UTC still said "yesterday". Date pickers, being timezone-blind, showed you that UTC date.

✅ THE FIX
All date-only form defaults now use a new local-calendar-day helper that reads your browser's own timezone. Whatever your computer clock says today is, the app agrees.
• Add Expense date → your local today
• Add Payment date → your local today
• Add Purchase date → your local today
• New Job → Date In → your local today
• Reports period presets → local calendar days (this one was already fixed last week for the P&L phantom-payment bug, and is now consolidated onto the same shared helper)

Full timestamps that represent moments in time (backup filenames, reconciled-at timestamps, signature timestamps) are intentionally left as UTC — those are supposed to be timezone-aware.

Nothing to do on your end — just refresh the browser and every form default will now match your clock.`
        },
        {
          question: 'Recent updates (July 2026 — part 10: Emergency Service invoice line item)',
          answer: `Bill your emergency / after-hours callouts as a distinct invoice line, then see exactly how much they brought in.

⚡ EMERGENCY SERVICE — inline invoice line (NEW Jul 17)
On the Job Detail page, in the Work & Billing panel, there's now an Emergency Service (inc GST — optional) input sitting immediately to the right of the Labor/Service Charge input.

How to use:
• Complete your emergency/after-hours job as usual.
• Fill in Labor/Service Charge (e.g. $580) for the base recovery work.
• Fill in Emergency Service (e.g. $250) for the emergency surcharge.
• Add any Stock items (e.g. 1TB replacement drive $130).
• The Summary auto-tallies everything: Subtotal (ex GST), GST, TOTAL (inc GST). The TOTAL is your grand total customer pays — no manual maths.
• Click Save (or Generate Invoice) — done.

The invoice PDF and preview now render Emergency Service as its own line right after your stock items, so the customer sees the surcharge broken out cleanly:
    SERVICES ................................. $580.00
    1TB Hard Drive (qty 1) ................... $130.00
    Emergency Service ........................ $250.00
                              Subtotal (ex GST): $872.73
                                    GST (10%):    $87.27
                                        Total:   $960.00

Leaving the field at 0 (default) hides it completely — no change to your existing invoices.

📊 EMERGENCY SERVICE REVENUE REPORT (NEW Jul 17)
On Reports → Profit & Loss, a new amber card "⚡ Emergency Service Revenue" appears at the bottom of the P&L (only when at least one job in the selected period had an emergency surcharge). It shows:
• Total inc-GST across the period
• Number of contributing jobs
• % of your total revenue

Click the card to drop-down a mini-table of the exact jobs (Job#, Customer, Type, Paid date, Emergency $). Click any row to jump straight to that job. Cash-basis (matches every other Financial figure) — a job is counted only once the customer has actually paid and the revenue_date is stamped.

Why not add it as a Job Type? Emergency is a *service level*, not a *service type* — an emergency SSD recovery is still an SSD recovery. Keeping it as its own field means Job Type, Priority, and reporting all stay clean and independent.`
        },
        {
          question: 'Recent updates (July 2026 — part 9: Approved-Ready-to-Schedule dashboard card)',
          answer: `New dashboard card so you never lose track of jobs that are Approved but still un-scheduled.

✅ APPROVED — READY TO SCHEDULE (NEW Jul 17)
On the Dashboard, right of "Not Proceeding" (in the empty white space of the Job Status Overview panel), a new green-bordered card now surfaces every job where:
• Status = Approved (customer has said yes — either remotely-signed or verbally-approved-then-set), AND
• Date Due is empty (i.e. you haven't booked it into the workshop schedule yet).

Each row shows: **#JobNumber · CustomerName · $Amount · Approved N minutes / hours / days ago**. The "Approved X ago" timestamp comes straight from the moment the customer clicked the Accept button on the remote quote link (from quote_history[].approval_date). Sorted newest-first, top 5 shown, with a "Showing 5 of N" badge when there's overflow.

Auto-hides completely when the count is 0 (no visual noise when nothing's pending). Click any row → jumps straight to the Job detail page so you can add the Date Due.

Why: previously an accepted quote could sit in your list for days without a due date — this makes the scheduling backlog impossible to miss.`
        },
        {
          question: 'Recent updates (July 2026 — part 8: Deposit tick + Personal-use tracking + form polish)',
          answer: `Latest wave — small workflow wins and a big EOFY reconciliation feature.

💵 DEPOSIT RECEIVED — one-click milestone (NEW Jul 15)
On any job whose quote is Accepted (either the customer signed remotely OR you flipped the status to "Approved" after a verbal OK), a blue dashed-border button "💵 Mark Deposit Received" now appears inline with the ✓ ACCEPTED badge inside the Quote card. One click:
• Timestamped comment auto-added to the timeline ("💵 Deposit received (marked by <you>)")
• Blue pill flips to solid: "💵 Deposit Received · 15 Jul" with a hover tooltip showing the full date-time + who marked it
• Click the solid pill to reverse — reversal is also logged (audit trail intact)
No payment record is created — this is a pure scheduling milestone. Job goes straight into your queue.

🏠 PERSONAL-USE CLAIMED — 3-way category selector (NEW Jul 15)
The old "Internal / Personal" on/off switch on the Expense and Purchase forms has been replaced with a 3-way dropdown, so you can now distinguish between "paid from business but NOT claimed" and "paid from business AND claimed but personal-use". Options:
• **Business (default)** — normal deductible expense → appears in P&L
• **Personal — Not Claimed** — excluded from P&L (same as the old "Personal" ticked)
• **Personal — Claimed** — kept in P&L (fully deductible) but flagged for your own tracking

Example: buy a $500 CCTV camera for home. Set it to "Personal — Claimed" → the $500 stays deducted from your net profit (you get the tax deduction), AND a new blue panel appears below NET PROFIT on the P&L showing:
    🏠 Personal-use items you claimed
    Computer Equipment: $500 (1)         Add-back (ex-GST): +$454.55
                                         True Business Profit: $10,454.55

The panel also lets you see the breakdown across categories (donations, equipment, etc.). Perfect for EOFY: know both the ATO number AND the "what my business really earned" number.

📦 NOT RECOVERABLE — Mark as Collected (NEW Jul 15)
Not Recoverable jobs now show the same red "📦 Ready for Return" panel + one-click "Mark as Collected" button that Not Proceeding jobs already had. No more clicking Edit → Close to hand the drive back.

🏷️ JOB STICKER POLISH (Jul 15)
• Removed the "#" — reads "Job 22850" instead of "Job #22850"
• Added a 1-line gap between the Job number and the customer name for better readability

📧 CC/BCC ON EVERY EMAIL TEMPLATE (Jul 15)
Every email template (Customer Templates, Quote Email, Custom Quote Templates, Invoice Email, Collection Notice, 2nd Warning / Final Notice, Job Booked Signature) now has Default CC / Default BCC fields. Great for self-archiving. When you pick a template on the Send dialog, its saved CC/BCC auto-fills the compose form (you can still override per-send).

🐛 QUOTE APPROVED TIMELINE — real amount shown
Fixed a bug where the "Quote Approved" timeline entry logged "$0.00 approved" for every remote signature. Now shows the real quoted amount ($780 in the example that caught it).

🛡️ EMAIL TEMPLATES CAN'T BE ACCIDENTALLY WIPED (Jul 15)
Added a self-heal on backend startup: any missing built-in email template is re-seeded from defaults, without ever overwriting your existing customizations. Peace of mind against partial config saves.`
        },
        {
          question: 'Recent updates (July 2026 — part 7: Jobs In workload tab + drill-in)',
          answer: `Big Reports upgrade — a full "workload / intake" view.

📥 JOBS IN — booking-basis workload tab (NEW Jul 10)
New Reports tab that answers "of ALL jobs booked in this period, how are they distributed?". Every job in the period is counted, regardless of status/paid state:
• Jobs by Status
• Jobs by Type
• Jobs by Brand
• Jobs by State
• Jobs by Referral Source

Every row is clickable → drops a filtered SortableJobsTable below (Job#, Customer, Status, Type, Brand, State, Date In, Total, View button). Drill count matches section row count exactly. Booking-date scoped (date_in), respects the top-of-page period selector.

Note: this counts ALL jobs booked in the period — including closed-same-day Not Proceeding / Not Recoverable / $0 Finish jobs — so your Jobs In number matches your diary / All Jobs count exactly.

🔗 REFERRAL SOURCES — 2-box redesign
Split into two clearly labeled boxes: "Leads by Source" (booking date — marketing view) and "Revenue by Source" (payment date — accounting view). No more mental gymnastics about which basis is which.

Small legend chips (💵 Payment date vs 📅 Booking date) now appear at the top of every Reports tab so you always know which axis you're looking at.

💰 P&L INCOME DRILL-DOWN (NEW Jul 10)
Click the Income line on the P&L → payment-method reconciliation drops down (Cash, Card, Bank Transfer breakdowns) so you can cross-check against your bank statement in one glance.`
        },
        {
          question: 'Recent updates (July 2026 — part 6)',
          answer: `Latest wave — P&L drill-in for consistent report exploration.

📊 CLICK ANY P&L CATEGORY → SEE ITS TRANSACTIONS (NEW Jul 8)
Every leaf row on Reports → Profit & Loss is now clickable. Click Postage / Freight / Software / Stock Purchases / any leaf → an inline mini table drops down showing every transaction that makes up that number (Date · Supplier · Payment Method · Total). Works for BOTH Operating Expenses AND Cost of Sales.

At the bottom of each drill, a "→ Open in Expenses page (full toolkit)" link jumps you to the Expenses / Purchases page with filters pre-set — for edits, reconciliation, breakdowns, or CSV export.

Every leaf now drills. Every report tab is consistent. No more "where does this number come from?"

🐛 PAYMENT STATUS "PAID" figure — fixed to match P&L exactly (see part 5 above for full detail).`
        },
        {
          question: 'Recent updates (July 2026 — part 5)',
          answer: `Critical bug fix — Payment Status now matches P&L income.

🐛 "PAID" FIGURE ACROSS ALL REPORTS — FIXED (Jul 8, 2026)
Symptom: P&L said $11,559 income for the last 7 days; Payment Status said Paid = $730. Same window, same jobs, wildly different numbers.

Root cause: The old code checked a \`payment_status\` field on each job that wasn't reliably kept in sync — many jobs with revenue collected (revenue_date set) never had the \`payment_status\` flag flipped to "Paid". So the Payment Status card was under-counting by 15 out of 16 jobs.

Fix: The 3 payment-state buckets are now DERIVED from your existing fields:
• 💰 **Paid** — \`revenue_date\` is set (the job has been closed and paid — matches P&L exactly).
• ⏳ **Awaiting Payment** — status is Finish / Not Recoverable / Not Proceeding, no revenue_date yet, and there's a balance owing. These are the jobs on your shelf waiting to be collected + paid. **This is your weekly chase list.**
• 🔧 **In Progress** — everything else (Booked, WIP, Approved, Wait — still being worked on).

Where this shows up:
• Reports → Payment Status: 3-row breakdown with click-to-drill.
• Dashboard: "⏳ Awaiting Payment" KPI card (click to open the chase list).
• Reports → Financial: "Finished Revenue (Paid)" card and all its variations.
• Every Job Detail page: pill badge under the Status showing the derived state (with tooltip explaining what it means).

Your workflow doesn't change — you keep using Finish / Not Recoverable / Not Proceeding exactly as before. The system now correctly identifies the "money owed & on shelf" set from your existing data without you needing to tag it manually.`
        },
        {
          question: 'Recent updates (July 2026 — part 4)',
          answer: `Latest wave — customer sign-off overhaul, report drill-ins, and reconciliation workflow.

✏️ TYPE-TO-SIGN — simpler customer signature (NEW Jul 7)
The freehand signature canvas is gone. Customers no longer have to draw with mouse/finger — a common cause of frustration, timeouts, and dropped signatures. Now:
• Customer opens the email link (or steps up to the iPad kiosk).
• Reviews the job details + Terms & Conditions.
• Ticks the "I agree" checkbox.
• Types their full name into an input field.
• Their name renders LIVE in a beautiful cursive font (looks like a real signature).
• Clicks "✓ Accept & Sign" — done.
Legally equivalent to a handwritten signature under the Electronic Transactions Act 1999 (Cth). Full audit trail (typed name + IP + user-agent + timestamp) captured server-side.

📊 CLICKABLE REFERRAL PARTNERS — drill-in like By Location
Click any partner row → Workflow Health bar, Status Breakdown chips, and sortable job list appear below with a View button per row. Same UX pattern as the By Location tab.

☑ RECONCILED CHECKBOX + "Show unreconciled only" (Expenses & Purchases)
Tick each expense/purchase as you cross-check it against your bank/CC/PayPal statement. Ticked rows highlight green + fade. Toggle "☐ Show unreconciled only" to hide finished work and focus on what's left. Progress counter ("2 / 47 reconciled — 4%") updates live. Persists to database — resume next month.

💳 CLICK-TO-FILTER Payment Methods Breakdown
Click any payment-method row in the breakdown card (Amex / Bank / PayPal / etc.) and the transactions table below instantly filters to just those. Perfect for statement reconciliation.

ℹ️ TOOLTIP on Payment Status "Payment date vs Booking date" toggle — hover the ℹ️ icon for a plain-English explanation of what each mode does.

🐛 JOB DISTRIBUTION COUNT BUG FIXED — Job Distribution / By Location / Priority / Referrals no longer hide unpaid jobs because of the Payment Status tab's cash-basis toggle.

🐛 REFERRAL PARTNERS report now filters by BOOKING date, not payment date — recent partner-referred jobs show up before they've been paid.`
        },
        {
          question: 'Recent updates (July 2026 — part 3)',
          answer: `Latest wave — visibility + self-service dropdown management.

💳 PAYMENT METHODS BREAKDOWN ON EXPENSES & PURCHASES
Both pages now show a "Payment Methods Breakdown" card between the summary tiles and the filter section. Grouped by method, showing:
• **# transactions**, **Subtotal (ex GST)**, **Total (inc GST)**, **% of total**.
• Respects the active date range, category / supplier, and Business/Internal filters.
• Perfect for questions like "How much did I put on the business Amex this quarter?" — filter to This Quarter, glance at the breakdown, done.

⚡ QUICK ENTRY TEMPLATES YOU CAN MANAGE YOURSELF
Both **Expense Quick Entry** and (new!) **Purchase Quick Entry** chips are now fully user-managed:
• Go to **Settings → Dropdown Values** — scroll to "Expense Quick Entry Templates" or "Purchase Quick Entry Templates".
• Click **+ Add Template**, fill in the fields, Save. New chip instantly appears at the top of the corresponding page.
• Click the red ✕ to remove any template.
• **Expense template fields**: Button Label · Category · Description · Payment Method.
• **Purchase template fields**: Button Label · Supplier (name) · Payment Method · optional Notes.
• Purchase Quick Entry auto-matches the supplier name to your existing suppliers — pre-fills the entire form on click.
• 5 default expense templates ship out of the box (Monthly Electricity / Monthly Rent / Vehicle Fuel / Internet Bill / Phone Bill). Delete or replace them freely.

💰 EDITABLE PAYMENT METHODS ON PURCHASES (also affects the templates above)
The Purchases → Record Purchase form now reads its Payment Method dropdown from **Settings → Dropdown Values → Payment Methods** (same list Expenses uses). Add "American Express", "PayPal", or anything else once — appears everywhere (Expenses, Purchases, Quick Entry templates).`
        },
        {
          question: 'Recent updates (July 2026 — part 2)',
          answer: `Newer wave — reporting alignment, newsletter compliance, editable dropdowns.

📮 NEWSLETTER CONSENT TRACKING (SPAM ACT COMPLIANT)
Every customer now has an explicit newsletter consent flag with source + timestamp.
• **Add Job form** — new "📮 Add customer to newsletter list" checkbox under Customer Type. Ticking it stamps consent with source="job_form" and today's date. Existing customers auto-pre-tick if they already consented.
• **Customers tab** — new "📮 Newsletter" column with 3 states: 🚫 Unsubscribed (permanent) · ✓ Consented (click for options) · + Opt-in (grey pill).
• **Newsletter sync** — Brevo sync now defaults to consented-only + NEVER returns unsubscribed customers. Toggle "include pending" in the sync UI to see non-consented contacts for review.
• Consent audit trail preserved: newsletter_consent_at, newsletter_consent_source ("job_form" / "manual" / "import"), newsletter_unsubscribed_at — an auditor can always answer "when + how did they opt in?".

🔌 BREVO UNSUBSCRIBE WEBHOOK — AUTOMATIC OPT-OUT SYNC
When customers click "unsubscribe" in a Brevo email, they're now instantly flagged in this app — no more manual sync.
• **Setup**: Newsletter → Settings → "Unsubscribe Sync (Webhook)" card. Click Generate to create a signing secret, copy the URL + secret into Brevo (Transactional → Settings → Webhooks), tick Unsubscribed / Hard bounce / Spam, Save.
• From then on, every unsubscribe / hard bounce / spam complaint auto-flags the customer as newsletter_unsubscribed = true.
• **Recent Events** table shows the last 25 events with email + result — useful for audit and troubleshooting.
• Signing secret prevents fake events being POSTed from anywhere on the internet.

💳 EDITABLE PAYMENT METHODS ON PURCHASES
The Purchases → Record Purchase form used to have a hardcoded dropdown (Cash / Card / Bank Transfer / Direct Debit / Credit Card / Other). It now reads from the SAME editable list as Expenses.
• Add "American Express", "PayPal", or anything else once in **Settings → Dropdown Values → Payment Methods** — it appears on both Expenses AND Purchases forms.
• Drag-and-drop to reorder. Delete to remove.

💰 DASHBOARD REVENUE — NOW MATCHES YOUR CREDIT CARD SLIPS
The Revenue Overview card was showing ex-GST subtotals — inconsistent with everything else. Now shows inc-GST cash-basis revenue.
• Numbers will jump ~10% — this is correct, you're now seeing actual money received.
• Filter uses \`revenue_date\` (day payment received), matching P&L / BAS.
• Click a specific day's bar → drill-in shows ONLY that day (previously showed the whole period).
• Drill-down modal leads with "Total (inc GST)"; ex-GST shown greyed for reference. "Finished" column renamed to "Paid on".

🎯 PAYMENT STATUS REPORT — BOOKING vs PAID TOGGLE
Payment Overview and Jobs by Payment Status used to filter by BOOKING date while every other revenue report used PAID date. Now aligned — with a toggle at the top of the tab.
• Default: 💰 Payment date (cash-basis) — matches P&L, BAS, Dashboard, Payment Methods Breakdown.
• Toggle to 📅 Booking date any time you want the "jobs booked this period regardless of payment" view.

📈 PAYMENT METHODS BREAKDOWN — DATE FILTER FIXED
The Money-In side was showing lifetime totals regardless of the date range — now filters by payment date. Money-In and Money-Out sides now agree.

🎨 PROGRESS STATUS ON DASHBOARD JOBS TABLE
The main jobs table on the Dashboard gains a Progress column right after Status, with colour-matched pills for Imaging (sky-blue) / WIP (amber) / Awaiting Parts (rose). "Not Started" shows a subtle grey dash so your eye focuses on active work.

🗓 CUSTOMER TYPES REPORT — "CUSTOMERS IN PERIOD" COLUMN + COPY EMAILS
Reports → Customer Types now has BOTH:
• 🌐 Customers (all-time) — lifetime unique customers per type.
• 📅 Customers (period) — unique customers who had a job in the selected date range.
The drill-in modal gets a "Show only customers with jobs in the selected date range" checkbox (defaults ON), plus a new **📋 Copy Emails** button that copies visible customers' emails (deduplicated, comma-separated) — ready to paste into your newsletter tool.

🐛 CUSTOMERS TAB DATE FILTER — NOW WORKS
The Last 7 Days / Quarter / Custom Range filter on the Customers tab was only shrinking job stats but still showing all 13,620 customers. Now correctly filters to customers CREATED in the selected period (based on \`created_at\`). Any preset works — great for "how many new End Users this month?".

🖥 WIDER WORKING AREA
The main content area now stretches up to 1600px (was 1400px) — job tables and dashboards breathe on 24"+ monitors. Zero visible change on laptops.

💳 DEFAULT PAYMENT METHOD = CARD
Record Payment now defaults to "Card" instead of Cash — matches the most common real-world method.

✏️ CC/BCC ON EMAIL TEMPLATES
Every Customer Email Template (Settings → Email Templates → Customer) now carries a Default CC + Default BCC. The Job Card → Email Customer dialog auto-fills them when you pick a template — still editable per-send. Every send is logged with the actual CC/BCC used.`
        },
        {
          question: 'Recent updates (July 2026)',
          answer: `Latest round of enhancements — payments, email templates, workflow polish.

📧 EMAIL CUSTOMER FROM JOB CARD (WITH TEMPLATES)
A brand-new **📧 Email Customer** button on every Job Card lets you fire off a generic, non-invoice email straight to the customer. Pick one of 4 seeded templates ("Additional Info Required", "Job Update", "Job On Hold — Awaiting Reply", "Quote Follow-up"), or write your own. All placeholders like {customer_name}, {job_number}, {device_info} are auto-substituted. Subject is auto-prefixed with **[Job #XXXX]** so the customer's inbox stays scannable. Sent emails are logged in the job's Email History card (full HTML body retained).

📝 CUSTOMER EMAIL TEMPLATES — FULL CRUD IN SETTINGS
**Settings → Email Templates → Customer Email Templates** now has an Add / Edit / Delete UI for the template library above. Use it to add your own templates or tweak the 4 defaults. See the "Email & SMS" section of this Help page for the full placeholder list.

✏️ EDIT PAYMENT (finally!)
Recorded a payment as **Cash** when it was actually **Credit Card**? Or typed $50 when you meant $500? You can now fix it. Each row in Payment History now has an **Edit** button next to Delete. Click it and the same Record Payment form opens in Edit mode with all fields pre-filled. Change amount / date / method / reference / notes → Update Payment. Totals recalculate. Activity Log captures the change in plain English ("amount $50.00 → $75.00, method Cash → Credit Card") so there's always a paper trail. Edits never retroactively re-open a closed job (accountant-safe).

🏷️ PRINT STICKER IN INVOICE ACTION ROW
After emailing an invoice, you no longer need to scroll back up to reprint the job sticker. The **🏷️ Print Sticker** button now sits inline with **Save / View Invoice / Print / PDF / Email / Regenerate** — one row, everything on hand.

🧹 TERMINAL STATUS AUTO-CLEARS PROGRESS
Marking a job as **Finish**, **Not Recoverable**, or **Not Proceeding** now automatically resets its Progress Status to **Not Started**. Finished jobs disappear from the Dashboard's Imaging / WIP / Awaiting Parts tiles the moment you mark them done — no manual cleanup needed.

📜 CHANGELOG (NEW)
The Help page header now has a **📜 Changelog** button that opens a searchable, filterable timeline of every change we've shipped. Newest first, filter by category (Feature / Enhancement / Bug Fix / UI), search by keyword. Live at Help → Changelog.`
        },
        {
          question: 'Recent updates (June 2026)',
          answer: `Latest round of improvements — printing, backups, workflow speed.

🏷 PRINT STICKER — EVERYWHERE YOU NEED IT
A "Print Sticker" button now appears on the Job Card header (next to Edit Job) so you can reprint a damaged sticker without leaving the job. The print-sticker logic is identical across Dashboard, All Jobs, and Job Card — fix it once, fix it everywhere.

🎯 PRINT ROTATION & TEST STICKER BUTTON
Settings → Job Sticker Settings now has:
• A Print Rotation dropdown (0° / 90° / 180° / 270°) for label printers whose driver flips orientation unexpectedly.
• A "Print Test Sticker" button so you can iterate on dimensions and rotation without finding a real job first.

🔌 DIRECT USB PRINTING (BETA)
If Chrome's print dialog and your Zebra driver are fighting (duplicate labels, blank labels, wrong rotation), you can now bypass them entirely. Settings → Job Sticker Settings → Direct USB Printing:
1. Click "Pair Zebra Printer" once (one-time browser permission)
2. Tick "Use Direct USB for Print Sticker"
3. Future Print Sticker clicks send raw EPL commands straight to the printer — no driver, no dialog

⚡ AUTO-UPDATING DEPLOYS (PWA fix)
Service worker rewritten — production now picks up new deploys automatically within seconds. No more "hard refresh after every redeploy" — open tabs reload on their own when a new version ships.

🧪 TESTING MODE BADGE
A small amber 🧪 TESTING MODE pill appears at the top-right corner whenever you're on the preview environment. On the live tdrau.online site the badge disappears — so you can tell at a glance whether you're testing or about to change live data.

📋 SAVE & PRINT STICKER ON NEW JOB
The Add Job form has a new button — "🏷️ Save & Print Sticker" — between Cancel and Save Job Only. One click saves the job AND sends the sticker to the printer.

📧 SAVE & SEND SIGNATURE LINK — NOW PRINTS THE STICKER TOO
The signature email dialog (when you book and email for signature) has a green "🏷️ Also print the job sticker now" checkbox, defaulted ON. One click now does everything: saves the job, emails the signature link, prints the sticker — and you don't have to reopen the job afterwards.

💾 BACKUP IMPROVEMENTS
• The "Last Backup" timestamp now updates on every backup type (manual, "Save to Server", "Create & Email", AND automatic). Previously only the nightly auto-backup updated this — so it looked like manual backups weren't running.
• Failed auto-backups now leave a visible red ❌ row in the backup history with the actual error message. No more silent failures.
• Confirmed: automatic backups run 7 days a week at the configured time.

💵 SMARTER QUOTE AMOUNT HANDLING
The big "$ Quoted Amount" headline in the customer email and approval page now hides itself when you leave the Quote Amount blank or $0. Perfect for multi-option quotes like "$325 recovery + $100 drive, OR customer-supplied drive". Fill the amount only when there's a single firm number; otherwise let your description do the talking.

✉️ DEFAULT PERSONAL MESSAGE FOR QUOTE EMAILS
Settings → Email Templates → Quote Email now has a "Default Personal Message" field. Click "📝 Insert Sample Template" to get started, edit to your voice, save. From then on every Send Quote dialog pre-fills with your message (with {customer_name}, {job_number}, {business_name}, {business_phone} replaced).

🐛 IMPORTED-JOB EDIT BUG FIXED
Editing an imported Jim2 job (e.g. changing Date Due) no longer shows the spurious "input should be a valid string" error. The change was always saving correctly — the error toast is now gone too.

🧹 JOB CARD HEADER CLEANUP
The Job Card header (title + 7 action buttons) now wraps cleanly on narrower screens instead of squashing the title.`,
        },
        {
          question: 'Recent updates (February 2026)',
          answer: `Here's what's been added since the last manual update — newest first.

📨 EDITABLE EMAIL TEMPLATES WITH EDIT-BEFORE-SEND
All three outbound customer emails — Invoice, Quote, and Job Booked – Signature Request — now use the same flexible template system.
• Defaults live in Settings → Email Templates: subject, body (full message, free-text), default CC, default BCC.
• At send time, an "Edit before send" dialog pre-fills from your template and lets you tweak Subject / Body / CC / BCC per customer.
• Placeholders supported: {customer_name}, {job_number}, {business_name}, {business_phone}, {business_email}, {device}.
• Activity log records every recipient including CC/BCC.

💼 INTERNAL / PERSONAL EXPENSE & PURCHASE FLAG
You can now tag any Expense or Purchase as "Internal / Personal" (e.g. headphones you bought on the business card for personal use).
• These items are EXCLUDED from the P&L by default so business numbers stay clean.
• On the Expenses and Purchases pages, three filter buttons let you switch: All / Business Only / Internal-Personal Only.
• On the Reports → P&L tab, tick "Include internal/personal" and a separate amber "Personal / Internal Items" section appears at the bottom of the P&L (with its own subtotal and 5th KPI card up top). Click any row or category to drill into the individual transactions.

📊 PER-CUSTOMER REVENUE BY PERIOD
Open any customer's card to see a Revenue by Period card above the job history with 6 quick tiles: This Month · Last Month · This Quarter · This Year · Last Year · All Time (highlighted).
• Plus a Custom Range strip where you can type any start / end date and click Calculate to see that customer's revenue + job count for that window instantly.
• Perfect for answering "how much work has DRC sent us in Q3?" in one glance.

🔎 SORTABLE COLUMN HEADERS ON ALL JOBS & CUSTOMERS
Click any column header (Job#, Customer, Status, Date In, Total, Total Spend, Jobs, etc.) to sort. Click again to flip ascending / descending. Arrows show the active column and direction. Sort happens server-side across the full 17k+ history, not just the visible page — so "Total Spend descending" really does surface your highest-spending customer ever.

⚡ SPEED IMPROVEMENTS
• 30+ MongoDB indexes added on jobs, customers, expenses, suppliers — most queries are now sub-200ms even on the full 17k-job dataset.
• Customers and All Jobs pages now load 200 rows by default with server-side search ("type to find anyone") and a "Show all" toggle for power users. Page load went from ~10s to ~1.5s.
• Global Search is faster (parallel queries) and lands you directly on the customer's card / supplier's edit dialog when you click a result.
• Reports tab feels noticeably snappier on every filter change.

📈 DASHBOARD "JOBS DUE SOON" — FULLY CLICKABLE
Click the bucket title, the big count, or "+N more" on any Jobs Due Soon card (Overdue / Due Today / Tomorrow / In 2-5 Days) to open a scrollable dialog with every job in that bucket. Each row is clickable → opens the job detail.

🗂 CUSTOMER TYPES — DRILL-IN SUMMARY REPORT
Reports → Customer Types now shows a full summary table per type (Customers / Jobs / Revenue / Avg per Customer) sorted by revenue. Click any row (Partner / End User / Not Specified) → modal listing every customer in that group with sortable columns and one-click navigation to their card.

🏷 TOTAL ROWS ADDED TO EVERY DISTRIBUTION TABLE
Status, Type, Brand, State, Referral Source, Priority — each table now shows a grey-highlighted Total row at the bottom. The Jobs by Type table also got a Revenue column.

🥧 PIE CHARTS CLEANED UP
Labels on small slices (< 5%) are hidden so they don't crowd each other. The full breakdown still appears in the legend below every pie.

📚 15-YEAR HISTORICAL JIM2 MIGRATION
Your full Jim2 history was imported: 17,400+ jobs, 13,500+ customers. Job types were reclassified from generic "Service" to meaningful categories (2.5 Data Recovery, 3.5 Data Recovery, SSD Data Recovery, RAID, USB Flash, SD Card, iPhone, Mobile Phone, Computer Repair, Data Erasure, etc.). Jobs older than 90 days were automatically marked Closed/Paid.

🖨 SMART PRINTER REMINDERS
Settings → Security → Printer Preferences lets you nominate which printer to use for stickers / job cards / invoices. Print dialogs show a banner reminding you which printer to send to.`
        },
      ]
    },
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: '🚀',
      items: [
        {
          question: 'How do I log in to the system?',
          answer: `To log in:
1. Navigate to the login page
2. Enter your username and password
3. Click the "Login" button

If you forget your password, contact your administrator to reset it.

**Tip:** Use the "Ctrl + K" keyboard shortcut anywhere in the app to quickly search for jobs, customers, or suppliers.`
        },
        {
          question: 'What is the Dashboard?',
          answer: `The Dashboard is your home screen that provides an overview of your business:

**Job Status Overview:** Clickable cards showing how many jobs are in each status (Booked, Quoted, Wait, Finish, etc.). Click any card to filter jobs by that status.

**Total Jobs:** Shows all active jobs. Click to clear filters.

**Awaiting Payment:** Displays jobs with outstanding balances.

**Jobs Due Soon:** Color-coded cards showing overdue jobs and upcoming deadlines.

**Reminders:** View and manage both job-related and general reminders.

**Revenue Overview:** Chart showing completed job revenue over time.

**Recent Activity:** Quick access to recently updated jobs.`
        },
        {
          question: 'How do I navigate the app?',
          answer: `**Desktop Navigation:**
Use the navigation bar at the top to access different sections: Dashboard, Add Job, All Jobs, Reminders, Customers, Suppliers, Stock, Purchases, Expenses, Reports, Newsletter, and Settings.

**Mobile Navigation:**
Tap the hamburger menu (☰) in the top-left corner to open the navigation panel.

**Quick Search:**
Press Ctrl + K (or Cmd + K on Mac) to open the global search and quickly find jobs, customers, or suppliers.`
        }
      ]
    },
    {
      id: 'jobs',
      title: 'Managing Jobs',
      icon: '📋',
      items: [
        {
          question: 'How do I create a new job?',
          answer: `To create a new job:
1. Click "Add Job" in the navigation bar
2. Fill in the customer details (name, address, contact info) — **email is optional**, leave blank for walk-in / mail-in customers with no email address
3. Enter job details (job type, brand, model, description)
4. Set dates (date in, estimated completion)
5. Click "Create Job"

**Tips:**
- Use the address autocomplete for faster entry
- Select an existing customer to auto-fill their details
- **Search-as-you-type + Add on the fly**: The **Job Type**, **Brand**, and **How did you find us?** fields all work the same way — start typing (e.g. "Max"). Matches from your saved list appear inline. If the value you need isn't in the list yet, a green **+ Add {name}** button appears next to the input — click it to persist the new entry to your settings for all future jobs.
- The job number is automatically generated based on your settings`
        },
        {
          question: 'How do I update a job status?',
          answer: `To change a job's status:
1. Open the job by clicking "View" or the job number
2. Click the "Edit" button
3. Select the new status from the dropdown
4. Click "Save Changes"

**Available Statuses:**
- **Booked:** Job has been received
- **Quoted:** Quote has been provided
- **Wait:** Waiting for customer approval
- **Approved:** Customer approved, work can begin
- **Parts:** Waiting for parts
- **Finish:** Work completed
- **Not Recoverable:** Cannot be repaired
- **Not Proceeding:** Customer declined`
        },
        {
          question: 'What is Progress Status and how does it work? (NEW Jul 2026)',
          answer: `**Progress Status** is an *operational* status separate from the customer-facing **Status**. It tracks what's actually happening on the bench right now.

**Available Progress Statuses:**
- **Not Started** (default) — nothing yet
- **Imaging** — currently taking an image of the drive
- **WIP (Work In Progress)** — active recovery/repair work
- **Awaiting Parts** — job is paused waiting for a component

**How to set it:**
1. Open the job detail page
2. Find the "Job Progress" dropdown in the Quick Edit section (top of page)
3. Pick the current progress state
4. Click **Save Changes**

**Where it shows up:**
The Dashboard has dedicated tiles for **Imaging**, **WIP**, and **Awaiting Parts** — clicking a tile shows every job currently in that state, so you can see at a glance what's on the bench.

**Auto-clear on completion (NEW Jul 2026):**
When you change the customer-facing Status to **Finish**, **Not Recoverable**, or **Not Proceeding**, the Progress Status **automatically resets to "Not Started"**. This means finished jobs disappear from the Dashboard operational tiles the moment you mark them done — no manual cleanup needed.`
        },
        {
          question: 'How do I add comments/notes to a job?',
          answer: `To add comments:
1. Open the job detail page
2. Scroll to the "Comments" section
3. Type your comment in the text area
4. Optionally check "Private Note" to hide it from customer communications
5. Click "Add Comment"

**Setting Reminders:**
When adding a comment, you can set a follow-up date. This creates a reminder that appears on your Dashboard.`
        },
        {
          question: 'How do I close or reopen a job?',
          answer: `**Closing a Job:**
From the Dashboard or job list, click the "Close" button on any job. Closed jobs are hidden from the Dashboard but remain accessible in "All Jobs."

**Reopening a Job:**
1. Go to "All Jobs"
2. Find the closed job (you may need to search)
3. Open the job and click "Reopen Job"

**Note:** Closing a job keeps all history intact for reporting purposes.`
        },
        {
          question: 'How do I delete a job?',
          answer: `**Jobs cannot be deleted from the app — this is intentional.**

Every job you create stays in the system permanently so your records, reports, and conversion-rate analytics remain accurate.

**Instead of deleting a job, change its status to:**
- **Not Proceeding** — customer decided not to go ahead (quoted, didn't accept).
- **Not Recoverable** — work attempted, data couldn't be recovered.
- **Finish** — completed successfully.

You can then close the job (button on the Dashboard) to hide it from your daily view while keeping the record intact for your reports.

**Why?** This protects you from accidental loss, supports clean conversion-rate reporting (e.g. "50 jobs in this month, 38 finished, 8 not proceeding = 76% close rate"), and ensures your job numbers remain in sequence without unexplained gaps.`
        },
        {
          question: 'How do I print a job form or sticker?',
          answer: `From the job detail page or job list:

**Print Full Form:** Click "Print" to generate a complete job form with all details, terms & conditions, and signature area.

**Print Sticker — three places it's available now:**
1. **Dashboard / All Jobs job cards** — the 🏷️ Sticker button on each card
2. **Job Card top action bar** — next to "Edit Job" (handy for reprinting a damaged sticker without leaving the job)
3. **Add Job form** — the 🏷️ Save & Print Sticker button saves the new job AND prints in one click

**Also when emailing for signature:** the Send Signature Link dialog has a "🏷️ Also print the job sticker now" checkbox (default on) so booking + sticker + email all happen in one click.

**Customize Sticker:** Settings → Security tab → Job Sticker Settings — set width/height in mm, font sizes for each line, and Print Rotation (0° / 90° / 180° / 270°) for printers whose driver flips orientation. Use the "Print Test Sticker" button to iterate without finding a real job.

**Direct USB Printing (Beta):** if your Zebra driver misbehaves (duplicate or rotated labels), tick "Use Direct USB for Print Sticker" in the same settings section. After a one-time "Pair Zebra Printer" click, sticker prints bypass Chrome's dialog and your Windows driver entirely — raw EPL goes straight to the printer.`
        },
        {
          question: 'When does revenue from a job count in my reports? (Cash Basis)',
          answer: `**Revenue is recognised on Cash Basis — exactly like Jim2.**

A job's revenue is counted in your Reports / P&L / BAS / Revenue Trends **only when**:
1. The job has Payment Status = **Paid** (fully paid, not a deposit)
2. AND the job has a **date_out** set (the day it left your office — collected/posted)

The cash-recognition date is the **date_out**, not the booking date or finished date.

**Example walkthrough:**
- Job booked 10 June, quoted 11 June, finished 20 June, customer collects + pays 5 July
- **In Jim2:** counts as July revenue ✅
- **In this app:** also counts as July revenue ✅ (date_out = 5 July)

**Deposits don't count yet** — if a customer gives you $300 deposit on 15 June and pays the $200 balance + collects on 5 July, the **full $500 counts in July** (when the job is finally marked Paid and date_out is set).

**Unpaid jobs never appear in revenue** — they sit on your books as outstanding. Cancelled / refunded / never-collected jobs simply don't contribute anything until they're closed and paid.

**Where it shows up:**
- ✅ Reports → Total Revenue / GST / All distribution charts
- ✅ Profit & Loss
- ✅ BAS Report (GST on Sales)
- ✅ Revenue Trends chart
- ✅ Customer card revenue history

**Where Cash Basis is indicated:** Look for the green **"CASH BASIS"** badge next to the "Reports & Analytics" title.

**Why this matters:** Your accountant works from your Xero bank feeds (also cash basis). Reports in this app will match what Xero shows you — and what Jim2 was telling you for the past 15 years.`
        },
        {
          question: 'How does "Not Recoverable" work with charging the customer?',
          answer: `**Yes — you can mark a job Not Recoverable AND still charge for your time. The system handles both correctly.**

This is the same workflow used in Jim2 and translates 1:1 into the new app.

**Example:** You attempted a recovery, couldn't recover the data, but spent 2 hours diagnosing. You charged the customer $350 for the work.
1. Set Job Status = **Not Recoverable**
2. Set Total Amount = **$350**
3. Set Payment Status to **Paid** (when collected)

**The $350 is counted as income everywhere it matters:**
- ✅ Reports → Jobs by Status (Not Recoverable row shows the revenue column)
- ✅ Reports → Revenue Trends and charts
- ✅ Profit & Loss Report (revenue ex-GST + GST collected)
- ✅ BAS Report (GST tracked)
- ✅ Customer card revenue history
- ✅ Jobs by Type / Location / Referral revenue columns

**Key rule — Status and Payment Status are independent:**
- **Status** (Booked, Not Recoverable, Finish, etc.) = operational state. Drives the "recovery success rate" analytics.
- **Payment Status** (Paid, Unpaid, Partial, Refunded) = financial state. Drives cashflow reports.

A job can be **Not Recoverable + Paid** at the same time, and the system tracks both correctly.

**Why this matters:** Don't be tempted to mark a charged-but-failed job as "Finish" just because money came in. Keeping it as Not Recoverable preserves your recovery success rate — one of the most important KPIs for a data recovery business. The money is still counted; the analytics stay honest.`
        }
      ]
    },
    {
      id: 'quotes',
      title: 'Quotes & Pricing',
      icon: '💰',
      items: [
        {
          question: 'How do I create a quote?',
          answer: `To create a quote for a job:
1. Open the job detail page
2. Scroll to the "Quotes" section
3. Click "Create Quote"
4. Add line items with descriptions and prices
5. The system automatically calculates GST
6. Click "Save Quote"

**Tips:**
- You can create multiple quote versions
- Compare quotes side-by-side
- Email quotes directly to customers`
        },
        {
          question: 'How do I email a quote to a customer?',
          answer: `To email a quote:
1. Open the job with the quote
2. Go to the Quotes section
3. Click "Email Quote" on the desired quote version
4. The Send Quote dialog opens with your default personal message pre-filled (set this default in Settings → Email Templates → Quote Email)
5. Tweak the message per-customer if needed, then click Send

**About the Quote Amount field:**
• When you enter a single amount, the customer email shows a big highlighted "$ Quoted Amount" headline.
• When you LEAVE IT BLANK or $0 (e.g. for a multi-option quote like "$325 recovery + $100 drive OR customer-supplied"), the headline is hidden and the customer reads your description instead. No more "$0" appearing on quotes.

**Setting up your default personal message:** Settings → Email Templates → Quote Email → click "📝 Insert Sample Template" → tweak to your voice → save. Placeholders: {customer_name}, {job_number}, {business_name}, {business_phone}.

The customer can view and approve the quote online using the link in the email.`
        },
        {
          question: 'How does customer quote approval work?',
          answer: `When you email a quote, the customer receives a link to:
1. View the quote details
2. See terms and conditions
3. **Type their full name** to electronically sign and approve (NEW Jul 7, 2026 — no more drawing with mouse/finger)
4. The job automatically updates to "Approved" status

You'll see the typed name rendered in cursive on the job detail page + a complete audit trail (IP address, timestamp, user-agent).`
        },
        {
          question: 'How is GST calculated?',
          answer: `GST is automatically calculated based on your settings:

**Default GST Rate:** Set in Settings > Business Info (typically 10% in Australia)

**Calculations:**
- **Total Amount:** The full price including GST
- **Subtotal:** The amount before GST (Total ÷ 1.1 for 10% GST)
- **GST Amount:** Total - Subtotal

You can view GST breakdowns on invoices and reports.`
        }
      ]
    },
    {
      id: 'payments',
      title: 'Payments & Invoicing',
      icon: '💳',
      items: [
        {
          question: 'How do I record a payment?',
          answer: `To record a payment:
1. Open the job detail page
2. Scroll to "Payment Tracking"
3. Click "Record Payment"
4. Enter the amount, payment method, and date
5. Optionally add a reference number and notes
6. Click "Save Payment"

**Payment Methods:** Cash, Card, Bank Transfer, PayPal, or custom methods you define in Settings.

**Partial Payments:** You can record multiple payments for a single job.`
        },
        {
          question: 'How do I edit or fix a payment I recorded incorrectly? (NEW Jul 2026)',
          answer: `If you recorded a payment against the wrong method (e.g. Cash instead of Credit Card), typed the wrong amount, or missed a reference number — you can now fix it without deleting and re-adding.

**To edit a payment:**
1. Open the job detail page
2. Scroll to "Payment History"
3. Click the **Edit** button next to the payment you want to correct
4. The Record Payment form re-opens in "Edit Payment" mode, pre-filled with the current values
5. Change any field: Amount, Date, Payment Method, Reference Number, or Notes
6. Click **Update Payment**

**What happens behind the scenes:**
- Amount Paid, Balance Owing, and Payment Status all recalculate automatically
- The original payment date/timestamp is preserved for audit purposes
- Activity Log captures the change in plain English, e.g. *"Payment edited (amount $50.00 → $75.00, method Cash → Credit Card)"* — so you always have a paper trail
- If the edit brings the job below fully-paid, the balance updates but the job stays closed (accountant-safe — no retroactive re-opening of closed jobs)

**To delete a payment instead:** click the red **Delete** button next to Edit.`
        },
        {
          question: 'How do I generate an invoice?',
          answer: `To create an invoice:
1. Open the job detail page
2. Click "Generate Invoice" (available when job has a total amount)
3. Review the invoice preview
4. Click "Download PDF" or "Email to Customer"

**Invoice Action Row (updated Jul 2026):**
Once an invoice is generated, you'll see one horizontal row of actions:
**Save | View Invoice | Print | PDF | Email | 🏷️ Print Sticker | Regenerate**

The **🏷️ Print Sticker** button is duplicated here (it's also at the top of the job) so after you email the invoice you can reprint the job sticker without scrolling back up. Uses the same printer settings as everywhere else.

**Invoice includes:**
- Your business details and logo
- Customer information
- Job details and line items
- GST breakdown
- Payment status and history
- Terms and conditions`
        },
        {
          question: 'How do I send collection reminders?',
          answer: `The system can automatically send collection reminders:

**Automatic Reminders (configure in Settings > Notifications):**
- First warning after X days
- Second warning after Y days
- Configurable email content

**Manual Reminders:**
1. Open the job
2. Click "Send Collection Email"
3. Choose the reminder type
4. Click "Send"

**Auto-Reminder Feature:**
Enable "Auto-reminder after second warning" in Settings to automatically create a follow-up reminder.`
        },
        {
          question: 'What payment statuses are available?',
          answer: `**Unpaid:** No payments recorded, full balance outstanding

**Partial:** Some payments recorded, balance remaining

**Paid:** Total amount has been fully paid

Payment status is automatically calculated based on recorded payments vs. total amount.`
        }
      ]
    },
    {
      id: 'customers',
      title: 'Customers & Suppliers',
      icon: '👥',
      items: [
        {
          question: 'How do I manage customers?',
          answer: `**Viewing Customers:**
Click "Customers" in the navigation to see all customers.

**Customer Details Include:**
- Contact information
- Address with autocomplete
- Job history
- Total revenue generated
- Notes

**Editing a Customer:**
Click on a customer name, then click "Edit" to update their details.`
        },
        {
          question: 'How do I add a new supplier?',
          answer: `To add a supplier:
1. Go to "Suppliers"
2. Click "Add Supplier"
3. Enter supplier details (name, contact, ABN)
4. Set default payment terms
5. Click "Save"

Suppliers are used for purchase orders and expense tracking.`
        },
        {
          question: 'Can I link customers to jobs automatically?',
          answer: `Yes! When creating a new job:
1. Start typing in the customer name field
2. Matching existing customers appear
3. Select a customer to auto-fill their details
4. Or continue typing to create a new customer

New customers are automatically saved for future use.`
        },
        {
          question: 'How do I produce a customer statement?',
          answer: `A **Customer Statement** is a summary of all unpaid jobs/invoices for one customer. Useful when chasing payment or sending an account summary on request.

**To generate a statement:**
1. Go to **Customers** in the top navigation
2. Click on the customer's name (or the "View" button) to open their detail page
3. Click the **"📄 Generate Statement"** button at the top of the customer page
4. A statement preview opens showing:
   - All unpaid invoices for that customer
   - Job number, date, description, and amount owing
   - Total balance owed across all jobs

**You can then:**
- **🖨️ Print** the statement (or save as PDF using your browser's print dialog)
- **📧 Email** it directly to the customer — the email goes to the address on file with a printable PDF attached
- **Close** the preview to discard

**Notes:**
- Statements only include jobs where balance owing > $0 (i.e., unpaid or partially paid jobs)
- Closed/fully-paid jobs are excluded
- The email uses your configured email service (Brevo or SMTP) from Settings → Email Config
- Each emailed statement is logged in the activity log on each job`
        }
      ]
    },
    {
      id: 'inventory',
      title: 'Stock & Inventory',
      icon: '📦',
      items: [
        {
          question: 'How do I manage stock items?',
          answer: `**Adding Stock Items:**
1. Go to "Stock"
2. Click "Add Item"
3. Enter item details (name, SKU, price, quantity)
4. Set reorder point for low stock alerts
5. Click "Save"

**Tracking Stock:**
- View current quantities
- See item history
- Track cost vs. selling price
- Monitor profit margins`
        },
        {
          question: 'How do I record a purchase?',
          answer: `To record a purchase:
1. Go to "Purchases"
2. Click "New Purchase"
3. Select the supplier
4. Add items and quantities
5. Enter invoice number and date
6. Click "Save"

Stock quantities automatically update when purchases are recorded.`
        },
        {
          question: 'How do I use stock items in jobs?',
          answer: `When creating a quote or invoice:
1. Click "Add Item from Stock"
2. Search or browse stock items
3. Select the item
4. Adjust quantity and price if needed

The item is linked to the job for tracking purposes.`
        }
      ]
    },
    {
      id: 'expenses',
      title: 'Expenses & Costs',
      icon: '📊',
      items: [
        {
          question: 'How do I record an expense?',
          answer: `To add an expense:
1. Go to "Expenses"
2. Click "Add Expense"
3. Select expense category
4. Enter amount and description
5. Set the date
6. Optionally link to a job
7. Click "Save"

**Expense Categories:** Customize categories in Settings > Dropdowns.

**Add on the fly:** if you start typing a category name that doesn't exist yet (e.g. \`3200 Owner Drawings\`), a green **+ Add {name}** button appears in the dropdown. One click saves the new category and pre-selects it — no need to leave the form. Same pattern applies to the **Supplier** field on the New Purchase form.`
        },
        {
          question: 'How do I link expenses to jobs?',
          answer: `When adding an expense, you can link it to a specific job:
1. Check "Link to Job"
2. Search for the job
3. Select the job

This allows you to track job profitability by seeing all associated costs.`
        },
        {
          question: 'How do I view expense reports?',
          answer: `Go to Reports and select the "Expenses" tab to see:
- Total expenses by category
- Monthly expense trends
- Category breakdown charts
- Detailed expense list with filters`
        },
        {
          question: 'How do I drill into P&L categories to see transactions? (NEW Jul 8, 2026)',
          answer: `Every leaf row on the Profit & Loss statement is now clickable.

**How to use:**
1. Go to **Reports → Profit & Loss** and pick your date range.
2. Expand a parent category (e.g. click ▶ next to Freight to reveal Postage).
3. Click any **leaf** row (e.g. Postage, Stock Purchases) — the ▶ turns to ▼ and a mini table drops down below showing every transaction that makes up that number.

**What the drill shows:**
- **For Operating Expenses**: Date · Description · Supplier · Payment Method · Total.
- **For Cost of Sales → Stock Purchases**: Date · Supplier · Invoice # · Payment Method · Total.

**"→ Open in Expenses/Purchases page" link** at the bottom of each drill jumps you to the full toolkit (edit, reconcile, delete, Payment Methods Breakdown, etc.).

**Tip:** The drill respects your date range filter. If you're on Last 7 Days you only see 7 days of transactions in the drill. Change the date range at the top to see more.

**Perfect for**:
- "Why is Postage $40.54 this month?" → click Postage → see the 3 postage bills that make it up.
- "What stock did I buy this quarter?" → set to This Quarter → click Stock Purchases → see every purchase invoice.
- "Something looks odd" → drill anywhere for instant verification.`
        },
        {
          question: 'What do 💰 Paid, ⏳ Awaiting Payment, and 🔧 In Progress mean? (NEW Jul 8, 2026)',
          answer: `The Payment Status report and the Dashboard "Awaiting Payment" card use three DERIVED buckets — you never need to set these manually. They come from your existing fields:

**💰 Paid** — The job has been closed and its \`revenue_date\` stamp is set. Money has landed in your bank/CC/PayPal. This is the figure that matches your P&L income and BAS.

**⏳ Awaiting Payment** — The job is done (status = Finish / Not Recoverable / Not Proceeding), no \`revenue_date\` yet, and there's a balance owing. In plain English: the drive is sitting on your shelf waiting for the customer to collect and pay you.
This is your **weekly chase list**. Click the Dashboard's ⏳ Awaiting Payment card to see them all.

**🔧 In Progress** — Everything else (status = Booked, WIP, Approved, Wait, etc.). Work is still happening on these jobs.

Your workflow is unchanged — keep marking jobs Finish / Not Recoverable / Not Proceeding when work stops. When the customer collects and pays, close the job — that stamps \`revenue_date\` and moves it into the 💰 Paid bucket automatically.

Deferred-payment exception: if a customer says "I'll pay next month, don't chase me", just add a note on the job. The job will remain in ⏳ Awaiting Payment (as intended) but you'll see the note when you go to chase.`
        },
        {
          question: 'How do I use the ⚡ Quick Entry chips? (NEW Jul 2026)',
          answer: `At the top of the **Expenses** and **Purchases** pages you'll see a **⚡ Quick Entry** card with one or more chip buttons.

**To use one:** Click a chip → the New Expense (or Record Purchase) form opens with the category, description, payment method (and supplier for purchases) already pre-filled. Just enter today's amount and save.

**To add your own chips:**
1. Go to **Settings → Dropdown Values**.
2. Scroll to **Expense Quick Entry Templates** or **Purchase Quick Entry Templates**.
3. Click **+ Add Template**.
4. Fill the fields:
   - **Expenses**: Button Label, Category, Description, Payment Method.
   - **Purchases**: Button Label, Supplier name (must match an existing supplier for the form to pre-fill), Payment Method, optional Notes.
5. Click **Add Template** — the new chip is instantly available.

**To remove a chip:** click the red ✕ in the templates table.

**Tip:** Use short button labels (e.g. "AWS Hosting", "Officeworks Restock", "Xero Subscription"). Long labels wrap awkwardly.`
        },
        {
          question: 'How do I reconcile against a bank/credit card statement? (NEW Jul 7, 2026)',
          answer: `Both **Expenses** and **Purchases** pages have a **Reconciled** checkbox column and a **☐ Show unreconciled only** filter to make month-end reconciliation fast and resumable.

**The 4-step workflow:**

1. **Filter to the period + method** — Set the date range (e.g. This Month), then click your credit-card / bank / PayPal row in the 💳 Payment Methods Breakdown card. The transactions table narrows to just those.

2. **Tick as you go** — Open your statement in another tab. For each statement line, find the matching row in the table and tick its ☑ box. The row highlights **green** and slightly fades. The counter at the top ("2 / 47 reconciled") updates live.

3. **Focus on what's left** — Click **☐ Show unreconciled only** to hide everything you've already ticked. Now you can see only the remaining rows.

4. **Fix discrepancies** — If a row doesn't match your statement, click **Edit** to correct the amount / date / GST, or **Delete** if it was a mistake. If your statement has an entry that's missing here, click **Add Expense** (or **Record Purchase**) and enter it.

**When everything matches:** the table will read *"All X matching expense(s) reconciled ✓"*. Done for the month.

**Resumable:** Close the tab. Come back tomorrow / next month. The ticks are stored in the database — pick up exactly where you left off.

**Tooltip on the checkbox** shows *when* it was reconciled and *who* did it — audit-friendly if you have multiple users.

**Tip:** For the fastest reconciliation, click each Payment Method row in turn (Amex, then Bank, then PayPal, etc.) — you clear one statement at a time.`
        },
        {
          question: 'How do I use the 💳 Payment Methods Breakdown? (NEW Jul 2026)',
          answer: `Between the summary tiles and the filter section on both **Expenses** and **Purchases** pages, you'll now see a **💳 Payment Methods Breakdown** card.

**What it shows** — for whatever filters are active (date range, category, supplier, business/internal):
- **Method** — Cash / Card / Bank Transfer / American Express / etc.
- **# Expenses (or Purchases)** — how many transactions used that method
- **Subtotal (ex GST)** — total spend excluding GST
- **Total (inc GST)** — the actual money out
- **% of total** — this method's share of the money out

Rows sort by Total descending (biggest first). A grey Total row at the bottom always sums to 100%.

**Click-to-filter (Reconciliation mode)** — NEW Jul 7:
Click any row (e.g. "American Express") and the transactions table below instantly filters to ONLY those transactions. A **blue "Filtered: X ✕" chip** appears at the top of the breakdown card. Click the ✕ (or the same row again) to clear.

**Month-end reconciliation workflow:**
1. Set date range to **This Month** (or last statement period).
2. In the 💳 Payment Methods Breakdown, click your credit-card / bank / PayPal row.
3. Cross-check the filtered list line-by-line against your statement.
4. Click Edit on any row that doesn't match; Delete/re-add if needed.
5. Click the ✕ chip → click another method row → repeat until every method's total matches its statement.

**Use it to answer:**
- "How much on the business Amex this quarter?" — filter to This Quarter, look for Amex row.
- "Did I spend more on Card or Bank Transfer this month?"
- "What % of my expenses go via Direct Debit?" — check the % column.

**Note:** The breakdown ONLY reflects filtered records. If you set a Category filter, it recalculates based on those filtered rows.`
        }
      ]
    },
    {
      id: 'reports',
      title: 'Reports & Analytics',
      icon: '📈',
      items: [
        {
          question: 'What reports are available?',
          answer: `**Financial Summary:** Overview of revenue, expenses, and profit

**Profit & Loss (P&L):** Detailed income statement with comparison periods

**BAS Report:** GST collected vs. paid for tax reporting

**Payment Reports:** Outstanding balances and collection rates

**Job Reports:** Jobs by status, type, and date range

**Customer Reports:** Revenue by customer, repeat business

**Stock Reports:** Inventory valuation and movement`
        },
        {
          question: 'How do I generate a Profit & Loss report?',
          answer: `To generate a P&L:
1. Go to Reports
2. Click the "Profit & Loss" tab
3. Select the date range
4. Optionally enable "Compare with previous period"
5. View or print the report

**Includes:**
- Income by category
- Expenses by category
- Gross profit
- Net profit
- Percentage changes`
        },
        {
          question: 'How do I export reports?',
          answer: `Most reports support multiple export options:

**Print:** Click the print button for a formatted printout

**PDF:** Download as a PDF document

**CSV:** Export data for use in Excel or other spreadsheets

Export buttons are located at the top of each report.`
        },
        {
          question: 'What is the BAS Report?',
          answer: `The BAS (Business Activity Statement) Report helps with tax reporting:

**GST Collected:** Total GST from your invoices/sales
**GST Paid:** Total GST on your purchases/expenses
**Net GST:** Amount owing or refundable

Filter by quarter or custom date range to match your BAS lodgment period.`
        }
      ]
    },
    {
      id: 'reminders',
      title: 'Reminders & Follow-ups',
      icon: '🔔',
      items: [
        {
          question: 'What types of reminders are there?',
          answer: `**Job Reminders:** Linked to a specific job, created when you add a comment with a follow-up date.

**General Reminders:** Standalone tasks not linked to any job. Perfect for general business tasks.

Both types appear on your Dashboard and the dedicated Reminders page.`
        },
        {
          question: 'How do I create a general reminder?',
          answer: `To create a general reminder:
1. From the Dashboard, click "+ Set Reminder"
2. Enter the task name
3. Add a description (optional)
4. Set the due date and time
5. Choose priority level
6. Click "Create"

Or go to the Reminders page and click "Add Reminder".`
        },
        {
          question: 'How do I complete a reminder?',
          answer: `To mark a reminder as complete:
1. Go to the Reminders page or Dashboard
2. Find the reminder
3. Click the checkmark or "Complete" button

Completed reminders are removed from your active list but can be viewed in history.`
        }
      ]
    },
    {
      id: 'ipad-kiosk',
      title: 'iPad Kiosk Mode',
      icon: '📱',
      items: [
        {
          question: 'What is Kiosk Mode?',
          answer: `Kiosk Mode allows customers to sign for jobs in person on a dedicated iPad or tablet:

1. Set up a tablet at your counter
2. Navigate to /kiosk on the tablet
3. When a customer is ready, push the job to the kiosk
4. Customer signs directly on the screen
5. Signature is automatically saved to the job`
        },
        {
          question: 'How do I send a job to the kiosk?',
          answer: `From the job detail page:
1. Click "Send to iPad" button
2. The job appears on your kiosk device
3. Hand the tablet to the customer
4. They review details and sign
5. Signature is captured and saved

**Auto-email option:** Enable in Settings to automatically email a signed copy.`
        },
        {
          question: 'How do I set up the kiosk device?',
          answer: `To set up your kiosk:
1. On your iPad/tablet, open the browser
2. Navigate to your app URL + /kiosk
3. Add to home screen for full-screen experience
4. The device polls for pending signatures

**Settings:** Configure thank-you message duration and auto-email in Settings > Kiosk.`
        }
      ]
    },
    {
      id: 'email-sms',
      title: 'Email & SMS',
      icon: '✉️',
      items: [
        {
          question: 'How do I configure email settings?',
          answer: `Go to Settings > Email to configure:

**Brevo (SendinBlue) Integration:**
1. Create a Brevo account
2. Get your API key
3. Enter it in Settings > Email
4. Configure sender name and email

**Email Types:**
- Quote emails
- Invoice emails
- Collection reminders
- Job completion notifications`
        },
        {
          question: 'How do I set up SMS notifications?',
          answer: `Go to Settings > SMS to configure:

**Twilio Integration:**
1. Create a Twilio account
2. Get your Account SID and Auth Token
3. Purchase a phone number
4. Enter credentials in Settings > SMS

**SMS Features:**
- Job status updates
- Collection reminders
- Custom messages`
        },
        {
          question: 'Can I customize email templates?',
          answer: `Yes! Go to **Settings → Email Templates** to customize:

- Subject lines
- Email body content
- Include/exclude specific information
- Add your logo and branding

Templates support placeholders like {customer_name}, {job_number}, etc.

**Two kinds of templates:**
1. **System templates** — Invoice Email, Quote Email, Signature Request Email (one of each; fires on the matching workflow).
2. **Customer Email Templates** *(NEW Jul 2026)* — a library of reusable templates for the general **"Email Customer"** button on any Job Card. Ships with 4 seeded defaults (see next question).`
        },
        {
          question: 'How do I use "Email Customer" from the Job Card? (NEW Jul 2026)',
          answer: `You can now send a **generic** email to a customer straight from any Job Card — no need to build an invoice first. Great for status updates, "we need more info", "job on hold", and quote follow-ups.

**To send an email:**
1. Open the job detail page
2. Click the **📧 Email Customer** button (in the header, next to "Add Comment")
3. Pick a **Template** from the dropdown — or leave it as *"-- Custom (write your own) --"* to type from scratch
4. Subject and body auto-fill with placeholders substituted for the current job's details
5. Edit anything you like before sending
6. Click **Send**

**Auto-prefix:** The subject is automatically prefixed with **[Job #12345]** so the customer's inbox scan reveals which job the email is about before they even open it.

**4 seeded defaults ship out of the box:**
- **Additional Info Required** — request missing info from the customer
- **Job Update** — quick "here's where we're at" note
- **Job On Hold — Awaiting Reply** — sent when you haven't heard back
- **Quote Follow-up** — polite nudge on an outstanding quote

**Sent emails are logged** in the job's Email History card (with the full HTML body), so you can review or resend later.`
        },
        {
          question: 'How do I manage Customer Email Templates? (NEW Jul 2026)',
          answer: `Go to **Settings → Email Templates → Customer Email Templates**.

**You can:**
- **Add** a new template — click *+ Add Template*, fill in Name / Subject / Body, optionally set Default CC / Default BCC, save.
- **Edit** any template — click the *Edit* button on the row.
- **Delete** a template — click *Delete* and confirm. (Templates already sent to customers aren't affected.)

**Default CC / Default BCC (NEW Jul 3, 2026)**
Each template can carry a **Default CC** and **Default BCC** — comma-separated lists of email addresses. When you pick that template on the Job Card, the CC and BCC fields in the compose dialog auto-fill from these defaults. Great for:
- Auto-archiving copies to yourself (put your own address in Default CC)
- Silently looping in your accountant on quote-related emails (put them in Default BCC — the customer can't see BCC)

You can still edit or clear CC/BCC before sending on any individual email.

**Placeholders you can use in Subject and Body:**
- \`{customer_name}\` — customer's full name
- \`{job_number}\` — the job number
- \`{device_info}\` — brand + model (or job type as fallback)
- \`{business_name}\` — your business name
- \`{business_phone}\` — your business phone
- \`{business_email}\` — your business email

**Important:** Do NOT include the job number in the Subject field yourself — the system prepends **[Job #XXXX]** automatically when the email is sent.

**Tip:** Duplicate a default template and tweak the tone for different scenarios (e.g. "Job Update — Positive Progress" vs "Job Update — Complication Encountered").`
        },
        {
          question: 'How does newsletter consent work? (NEW Jul 6, 2026)',
          answer: `The app now tracks explicit newsletter consent per customer — required under Australia's Spam Act 2003.

**Where consent is captured:**
- **Add Job form** — new checkbox "📮 Add customer to newsletter list" under Customer Type. Tick it during booking to record consent with source = "job_form".
- **Customers tab** — click the grey "+ Opt in" pill next to any customer to manually opt them in (source = "manual").

**Three states in the Customers "📮 Newsletter" column:**
- 🚫 **Unsubscribed** (red pill) — permanent. This customer will NEVER be included in a newsletter sync, even if you tick the Job form checkbox on a repeat booking.
- ✓ **Consented** (green pill) — included in Brevo syncs. Click it to reveal a "Mark UNSUBSCRIBED" option if you need to opt them out manually.
- **+ Opt-in** (grey dashed pill) — not yet consented. Click to opt them in.

**Consent audit trail** (always preserved):
- \`newsletter_consent_at\` — when they first said yes
- \`newsletter_consent_source\` — "job_form" / "manual" / "import"
- \`newsletter_unsubscribed_at\` + \`newsletter_unsubscribed_reason\` — when + why (unsubscribe click / hard bounce / spam)

**Sync behaviour**: Newsletter → Sync Contacts now defaults to consented-only. Unsubscribed customers are ALWAYS excluded — no override.`
        },
        {
          question: 'How do I set up the Brevo unsubscribe webhook? (NEW Jul 6, 2026)',
          answer: `When customers click "unsubscribe" in a Brevo email, this app can now flag them automatically — no more manual sync.

**One-time setup (5 minutes):**

1. **In this app**: go to **Newsletter → Settings** and scroll to the "Unsubscribe Sync (Webhook)" card.
2. Copy the **Webhook Endpoint URL** shown (it points to your app's /api/webhooks/brevo).
3. Click **Generate** under Signing Secret and copy the value shown (⚠️ it's shown only once — copy it now).
4. **In Brevo**: log in and go to **Transactional → Settings → Webhooks** → **Add a new webhook**.
5. Paste the URL into the URL field. Paste the secret into the "Secret" field.
6. Tick these events: **Unsubscribed**, **Hard bounce**, **Spam**.
7. Save.
8. *(Optional, for marketing campaigns)*: also add a webhook under **Contacts → Settings → Unsubscribe webhooks** with the same URL + secret.

**What happens after setup:**
- Every time a customer clicks "unsubscribe" in one of your Brevo emails, Brevo POSTs an event to us.
- The customer is instantly flagged \`newsletter_unsubscribed = true\` with timestamp + reason.
- Their newsletter consent is revoked. They'll never appear in a future sync.
- Hard bounces (invalid mailboxes) and spam complaints also trigger opt-outs — you stop wasting sends on dead addresses.

**Audit trail**: the same card shows a "Recent Events" table with the last 25 events. Green badge for logged-only events (opens, deliveries); red destructive badge for opt-outs.

**Rotating the secret**: if you ever suspect the secret leaked, click **Rotate** to generate a new one — then update Brevo with the new value. The old one stops working immediately.`
        }
      ]
    },
    {
      id: 'settings',
      title: 'Settings & Configuration',
      icon: '⚙️',
      items: [
        {
          question: 'How do I update business information?',
          answer: `Go to Settings > Business Info to configure:

- Business name
- Address
- Phone and email
- Website
- ABN
- Logo upload
- Default GST rate`
        },
        {
          question: 'How do I customize dropdowns?',
          answer: `Go to Settings > Dropdowns to manage:

- Job statuses (with colors)
- Job types
- Brands
- **Payment methods** (applies to BOTH Expenses AND Purchases — add "American Express", "PayPal", etc. here to make them selectable on both forms)
- Expense categories
- **Accessory Options** (one-tap chips on the New Job form)

You can add, remove, and reorder items by dragging.

**Tip:** If you rename or remove a status/job type/brand that's already in use by existing jobs, those jobs will display the original value tagged "(legacy)" inside the edit dropdown so you don't lose data — you can pick a new value or keep it.`
        },
        {
          question: 'How do I manage Accessory Options (the chips on the New Job form)?',
          answer: `The Accessories field on the New Job form uses one-tap suggestion chips so you don't have to type common items (Cables, Power Supply, Spare Drive, Case, etc.). You can fully customise this list:

**To add a new chip:**
1. Go to Settings > Dropdowns
2. Scroll to "Accessory Options"
3. Click "Add New"
4. Type the name (e.g., "Adapter", "Stylus Tip") and press Enter

**To remove a chip:**
- Click "Remove" next to any option you no longer need.

**To reorder chips:**
- Drag any item up or down — the order is reflected on the New Job form.

**On the New Job form:**
- Tap a chip to add that accessory (it turns dark with a checkmark)
- Tap it again to remove
- Use the "Other accessory" input for one-off items not in your chip list
- Selected items appear as removable blue tags below

**Behind the scenes:** Accessories are now stored as a structured list (not free text), so they render cleanly on PDFs, job cards, and the iPad kiosk signature page. Existing legacy jobs were automatically migrated.`
        },
        {
          question: 'How do I manage users?',
          answer: `Go to Settings > Users (Admin only):

**Add User:**
1. Click "Add User"
2. Enter username and password
3. Select role (Admin or User)
4. Click "Save"

**User Roles:**
- **Admin:** Full access to all features including settings
- **User:** Limited access, cannot modify settings or users`
        },
        {
          question: 'How do I backup my data?',
          answer: `Go to Settings > Backup & Restore:

**Create Backup:**
1. Click "Create Backup"
2. Download the backup file
3. Store it securely

**Restore from Backup:**
1. Click "Restore"
2. Select your backup file
3. Confirm the restore

**Note:** Backups include all jobs, customers, settings, and data.`
        },
        {
          question: 'How do I enable Two-Factor Authentication (2FA)?',
          answer: `2FA adds a second layer of security on top of your password — required to keep customer and financial data safe online.

**To enable:**
1. Go to Settings > Security
2. Click "Enable 2FA"
3. Scan the QR code with Google Authenticator, Microsoft Authenticator or Authy on your phone
4. Enter the 6-digit code from your app to confirm
5. **(Important)** Save the 10 recovery codes — print them or copy to a password manager. Each works once.

**On your next sign-in:**
- Enter your username + password as usual
- A second screen will ask for the 6-digit code from your phone
- Tick "Remember this device for 30 days" so the same computer skips 2FA for the next month

**If you lose your phone:**
- Use a recovery code from the 10 you saved
- Or click "Use email code" / "Use SMS code" to receive a one-time code at your recovery email/phone (set these in Settings > Security)

**Other 2FA controls (Settings > Security):**
- View remaining recovery codes (regenerate to invalidate the old set)
- Manage trusted devices (revoke any computer you no longer want remembered)
- Update recovery email / phone
- Disable 2FA (requires password + current authenticator code)`
        }
      ]
    },
    {
      id: 'keyboard',
      title: 'Keyboard Shortcuts',
      icon: '⌨️',
      items: [
        {
          question: 'What keyboard shortcuts are available?',
          answer: `**Global Shortcuts:**
- **Ctrl + K** (or Cmd + K): Open global search
- **Escape:** Close modals and dialogs

**Navigation:**
- Use Tab to move between fields
- Enter to submit forms
- Escape to cancel

**In Forms:**
- Tab through fields
- Use dropdowns with arrow keys`
        }
      ]
    },
    {
      id: 'backups-drive',
      title: 'Backups & Google Drive',
      icon: '☁️',
      items: [
        {
          question: 'How do I create a backup of my data?',
          answer: `1. Go to **Settings → Backup & Restore**
2. In the "Manual Backup" section, click **"Create Backup Now"**
3. A ZIP file is created containing every job, customer, supplier, stock item, purchase, expense, settings, users and counters
4. The new backup appears in the **Backup History** table
5. Click **"Download"** on the row to save the ZIP to your computer

**Best practice:** Always keep at least one ZIP downloaded to your computer in case server-stored backups are lost.`
        },
        {
          question: 'How do I restore from a backup?',
          answer: `Two ways to restore:

**Option A — From an existing server backup**
1. Settings → Backup & Restore → Backup History
2. Click the **🔄 Restore** button on a row
3. Confirm — all data is replaced with the backup's contents

**Option B — From a downloaded ZIP (most common, for disaster recovery)**
1. Settings → Backup & Restore → **"Restore from Upload"** section
2. Click "Choose File", pick the ZIP from your computer
3. Confirm

⚠️ Restore **replaces** all current data. Anything created/changed since the backup will be lost.

✅ Drive credentials and backup-history file rows are **not** wiped, so your Drive connection survives.`
        },
        {
          question: 'How do I connect Google Drive for automatic backups?',
          answer: `1. Go to **Settings → Backup & Restore**
2. Find the **☁️ Google Drive Auto-Backup** card
3. Click **"Connect Google Drive"** — a new tab opens
4. Sign in with the Google account whose Drive you want to use
5. Click **"Advanced → Go to (unsafe)"** if Google warns about an unverified app (safe for your own app)
6. Grant permission
7. The new tab auto-closes; the app refreshes showing "✓ Connected"
8. Set your folder name (default: "Emergent Backup") and click **Save Drive Settings**
9. The auto-upload checkbox should be ticked

From now on, **every backup automatically uploads to your Drive**.`
        },
        {
          question: 'How do I manually upload an old backup to Drive?',
          answer: `If a backup was created before Drive was connected, or auto-upload was off:
1. Find the backup row in Backup History
2. Click the **☁️** (cloud) icon in the Actions column
3. Wait a moment — it turns into a green **☁️✓** once uploaded
4. Click the green tick to open the file in Google Drive`
        },
        {
          question: 'Where do backups go in my Google Drive?',
          answer: `Backups are uploaded to a folder you configure (default: **"Emergent Backup"**) in your Drive's root.

**Important note about the folder:**
The app uses Google's *drive.file* scope (the least-privilege option), which means **the app can only see folders/files it has created itself**. So:
- ✅ If you let the app create the folder, it works perfectly
- ❌ If you manually create a folder with the same name beforehand, the app won't see it and will create its own "Emergent Backup" alongside

Simplest approach: just leave the default and let the app create the folder.`
        },
        {
          question: 'How do I reset the entire database?',
          answer: `⚠️ **This permanently deletes all jobs, customers, suppliers, stock, purchases, expenses, reminders, and newsletter history.** Settings, user accounts, and Drive credentials are preserved.

**Before resetting:**
1. **Download your latest backup ZIP to your computer** (the reset also deletes server-stored backups)
2. Make sure you've tested restore at least once

**To reset:**
1. Settings → Backup & Restore → **Database Reset** section
2. Read the warning carefully
3. Type **"RESET"** into the confirmation box
4. Enter your admin password
5. Click **🗑️ Reset Database**

**After reset:**
- Dashboard is empty
- Job number counter resumes from your configured starting value (Settings → Job Numbers)
- You can restore from your downloaded ZIP to bring everything back

**Recommended cut-over workflow:** Reset → fresh start (no restore) — perfect for a clean go-live.`
        }
      ]
    },
    {
      id: 'internal-notes-activity',
      title: 'Internal Notes & Activity Log',
      icon: '📝',
      items: [
        {
          question: 'How do internal notes work?',
          answer: `Internal notes are private comments you make about a job — **never** shown to customers or on invoices.

**To add a note:**
1. Open the job detail page
2. Click **🔒 Internal Notes** button (look for a green number badge showing how many notes exist)
3. Type your note in the "Add a new internal note" box
4. Click **💾 Save Note**

The note is automatically timestamped with the date/time and your username. The input clears, ready for the next note.

**Notes History:**
- All notes appear below, **newest at the top**
- Each shows who wrote it, when, and the full text
- Click the **🗑️** trash icon to delete an individual note (with confirmation)`
        },
        {
          question: 'What is the Job Activity Log?',
          answer: `The Activity Log shows a complete timeline of everything that happened to a job — useful for audit, dispute resolution, and remembering what was done when.

**Tracked automatically:**
- Job created
- Status changes
- Comments added
- Quote saved / sent / accepted / rejected
- Email & SMS sent (with recipient)
- Payment recorded
- Customer signed
- Total amount adjusted
- Internal note added/deleted

**To view:** Open any job → scroll down to the **📋 Activity Log** section. Newest events at the top.`
        }
      ]
    },
    {
      id: 'invoices-payments',
      title: 'Invoice Workflow',
      icon: '📄',
      items: [
        {
          question: 'How do I send a customer their invoice?',
          answer: `Three options:

**📧 Email** — Most common. The invoice PDF is attached and sent to the email on the customer's record. Each email is logged in the job's Email History and Activity Log.

**📄 PDF** — Opens the generated PDF in a new tab so you can review/save it.

**🖨️ Print** — Opens the invoice in a new window and auto-triggers your browser's Print dialog. Use this when a customer is physically in your shop. If pop-ups are blocked, allow them for the site.

All three use the same invoice layout, so the result is identical regardless of which delivery method you pick.`
        },
        {
          question: 'How do I write off a job (e.g., declined quote)?',
          answer: `If a customer declines a quote or the job is cancelled, you can zero out the invoice so it stops appearing in "Awaiting Payment":

1. Open the job
2. In the **Work & Billing** section, find the Job Total line
3. Click the small **(adjust)** link next to the total
4. Enter the new total (e.g. **0** to write off completely)
5. Optionally add a reason
6. Click **Update Amount**

The change is logged in the Activity Log as "Total amount adjusted: $X → $Y" so you have an audit trail.`
        },
        {
          question: 'How do I record a partial or full payment?',
          answer: `1. Open the job
2. Scroll to the **Payment Tracking** section
3. Next to the "Unpaid" / "Partial" badge, click the green **+ Add Payment** pill
4. Enter:
   - Amount paid
   - Payment date
   - Payment method (Cash, Card, Bank Transfer, etc.)
   - Optional reference (transaction ID, receipt number)
5. Click **Save Payment**

**What happens automatically:**
- Balance owing updates
- Payment status changes (Unpaid → Partial → Paid)
- If status reaches **Paid**, the job is auto-closed and disappears from "Awaiting Payment"`
        }
      ]
    },
    {
      id: 'customer-statements',
      title: 'Customer Statements',
      icon: '🧾',
      items: [
        {
          question: 'How do I produce a customer statement?',
          answer: `A Customer Statement is a summary of all unpaid jobs/invoices for one customer. Use it when chasing payment or sending an account summary on request.

**To generate:**
1. **Customers** in the top navigation
2. Click the customer's name to open their detail page
3. Click **📄 Generate Statement** at the top
4. A preview opens showing all unpaid invoices for that customer

**From the preview you can:**
- **🖨️ Print** the statement
- **📧 Email** it (PDF attached, sent to address on file — logged in each related job's activity log)
- **Close** to discard

**Notes:**
- Only jobs with balance owing > $0 are included
- Closed/fully-paid jobs are excluded
- Email uses your configured email service (Brevo or SMTP) in Settings`
        }
      ]
    },
    {
      id: 'search-typeahead',
      title: 'Search & Quick Entry',
      icon: '🔍',
      items: [
        {
          question: 'How do I search for suppliers and stock items?',
          answer: `Both pages now have **live search bars** at the top:

**Suppliers** — searches name, contact person, phone, email, ABN
**Stock** — searches name, brand, item code, SKU, category, description

Type any partial word — the list filters as you type. Click **Clear** to reset.

The count in the card title updates dynamically (e.g., "All Stock Items (12)" shows only the matching count).`
        },
        {
          question: 'How does typeahead work for entering purchases and expenses?',
          answer: `When recording an **Expense**:
- **Category** — start typing the account number (e.g. "6401") OR the name (e.g. "Council") → matching options appear → click to select
- **Supplier** — same: type to search, ✕ to clear

When recording a **Purchase**:
- **Supplier** — type to search; if not found, click "+ New" to create on the fly
- **Stock Item** (per line) — search by brand, item name, item code, or SKU; "+ New" creates a new stock item without leaving the form

This is much faster than scrolling through dropdowns. The "+ New" buttons let you create suppliers and stock items mid-purchase without losing your work.`
        }
      ]
    },
    {
      id: 'reports-revenue',
      title: 'Reports & Revenue Drill-Down',
      icon: '📊',
      items: [
        {
          question: 'How do I see which jobs make up my revenue?',
          answer: `The **Revenue Overview** widget on the Dashboard is fully clickable:

1. Click the **Total Revenue** $ amount → modal opens with all completed jobs in the period
2. Click the **Jobs Completed** number → same modal
3. Click any **bar** in the chart → drill-down for the selected period

**The modal shows:**
- Each job's #, customer, type, finished date
- Subtotal (ex GST) and Total (inc GST)
- Payment status (color-coded pill)
- An **Open** button to jump straight to the job detail page

**Switch periods** using the dropdown at the top of the Revenue Overview card — Weekly, Monthly, or custom date range.`
        }
      ]
    },
    {
      id: 'purchases-edit',
      title: 'Editing & Deleting Purchases',
      icon: '🛒',
      items: [
        {
          question: 'How do I edit or delete a purchase?',
          answer: `On the **Purchases** page, each row now has three buttons:

- **View Details** — read-only summary
- **Edit** — opens the same form you used to record the purchase, pre-filled. Change any field (supplier, items, quantities, costs, GST, notes). Save to update.
- **Delete** (red) — removes the purchase with confirmation

**Important — stock reconciliation:**
- **Editing**: stock quantities first revert to the pre-purchase state, then the new quantities are applied. The system recalculates the weighted-average cost price too.
- **Deleting**: stock quantities added by this purchase are reverted (the items go back to their previous count).

So if you originally bought 10 units and edit it down to 5, the net effect is -5 from current stock.`
        }
      ]
    },
    {
      id: 'security-ip',
      title: 'IP Access Control & Security',
      icon: '🔒',
      items: [
        {
          question: 'How do I lock the app to my office IP only?',
          answer: `⚠️ **Only enable this AFTER you've deployed to your custom domain.** While running on the preview URL, the proxy masks your real IP and you can lock yourself out.

**Once on your custom domain:**
1. Settings → **IP Access Control**
2. Tick "Enable IP restriction"
3. Click "Add my current IP" — it auto-detects your office IP
4. Add any other IPs (home office, second branch) if needed
5. Save

**Mobile/remote access:**
- Generate a **Bypass Code** — paste it as a URL parameter when away from the office (e.g., **?access=YOUR_CODE**)
- Optionally tick "Remember device" so you don't need the code each time on that browser`
        }
      ]
    },
    {
      id: 'about-this-app',
      title: 'About This App',
      icon: 'ℹ️',
      items: [
        {
          question: 'What version of the app am I running?',
          answer: `This is a full-stack Progressive Web App custom-built for Total Data Recall.

**Tech stack:**
- Frontend: React + Tailwind + Shadcn UI
- Backend: Python FastAPI
- Database: MongoDB

**Hosting:** Emergent platform (auto-managed deployment, SSL, scaling)

**External integrations:**
- 📧 Brevo (transactional emails)
- 📱 Twilio (SMS notifications)
- ☁️ Google Drive (backup uploads)
- 🗺️ Google Places (address autocomplete)`
        },
        {
          question: 'Who can I contact for help?',
          answer: `For **app issues, bugs, or feature requests**: contact Emergent support via app.emergent.sh

For **business-specific data questions** (e.g., "where did this customer record come from?"): that's your own data — check the Activity Log on the relevant job for the audit trail.

**Self-help:**
- Use this in-app Help guide (you're using it now!)
- Search by keyword at the top of this page
- Download the printable PDF manual for offline reference`
        }
      ]
    }
  ];

  // Filter help items based on search
  const filteredSections = useMemo(() => {
    if (!searchTerm.trim()) return helpSections;
    
    const term = searchTerm.toLowerCase();
    return helpSections.map(section => ({
      ...section,
      items: section.items.filter(item => 
        item.question.toLowerCase().includes(term) ||
        item.answer.toLowerCase().includes(term)
      )
    })).filter(section => section.items.length > 0);
  }, [searchTerm]);

  // Count total help items
  const totalItems = helpSections.reduce((acc, section) => acc + section.items.length, 0);

  // Handle PDF download
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await fetch(`${API}/help/download-manual`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Total_Data_Recall_User_Manual.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        // Fallback: open printable version
        window.open(`${API}/help/printable-manual`, '_blank');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      // Fallback: open printable version
      window.open(`${API}/help/printable-manual`, '_blank');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="help-page" style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 1rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          style={{ marginBottom: '1rem', padding: '0.5rem' }}
        >
          <ArrowLeft size={18} style={{ marginRight: '0.5rem' }} />
          Back
        </Button>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem' }}>
              Help & User Guide
            </h1>
            <p style={{ color: '#64748b' }}>
              Everything you need to know about using Total Data Recall
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Button
              variant="outline"
              onClick={() => navigate('/help/changelog')}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              data-testid="open-changelog-btn"
            >
              📜 Changelog
            </Button>
            <Button 
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              data-testid="download-manual-btn"
            >
              <Download size={18} />
              {downloadingPdf ? 'Preparing...' : 'Download PDF Manual'}
            </Button>
          </div>
        </div>
      </div>

      {/* Search */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <CardContent style={{ padding: '1rem' }}>
          <div style={{ position: 'relative' }}>
            <Search 
              size={18} 
              style={{ 
                position: 'absolute', 
                left: '0.75rem', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#94a3b8'
              }} 
            />
            <Input
              placeholder="Search help topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '2.5rem' }}
              data-testid="help-search-input"
            />
          </div>
          {searchTerm && (
            <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#64748b' }}>
              Found {filteredSections.reduce((acc, s) => acc + s.items.length, 0)} results
            </p>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <Card style={{ textAlign: 'center', padding: '1rem' }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1e40af' }}>
            {helpSections.length}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Topics</div>
        </Card>
        <Card style={{ textAlign: 'center', padding: '1rem' }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#10b981' }}>
            {totalItems}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Articles</div>
        </Card>
        <Card style={{ textAlign: 'center', padding: '1rem' }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#f59e0b' }}>
            <FileText size={32} style={{ margin: '0 auto' }} />
          </div>
          <div style={{ fontSize: '0.875rem', color: '#64748b' }}>PDF Available</div>
        </Card>
      </div>

      {/* Help Sections */}
      {filteredSections.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '3rem' }}>
          <p style={{ color: '#64748b' }}>No help topics found for &ldquo;{searchTerm}&rdquo;</p>
          <Button variant="outline" onClick={() => setSearchTerm('')} style={{ marginTop: '1rem' }}>
            Clear Search
          </Button>
        </Card>
      ) : (
        filteredSections.map((section) => (
          <Card key={section.id} style={{ marginBottom: '1rem' }} data-testid={`help-section-${section.id}`}>
            <CardHeader style={{ paddingBottom: '0.5rem' }}>
              <CardTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>{section.icon}</span>
                {section.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {section.items.map((item, index) => (
                  <AccordionItem key={index} value={`${section.id}-${index}`}>
                    <AccordionTrigger style={{ textAlign: 'left', fontSize: '0.95rem' }}>
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div 
                        style={{ 
                          whiteSpace: 'pre-line', 
                          lineHeight: '1.7',
                          color: '#374151',
                          fontSize: '0.9rem',
                          padding: '0.5rem 0'
                        }}
                      >
                        {item.answer}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        ))
      )}

      {/* Footer */}
      <Card style={{ marginTop: '2rem', marginBottom: '2rem', background: '#f8fafc' }}>
        <CardContent style={{ padding: '1.5rem', textAlign: 'center' }}>
          <p style={{ color: '#64748b', marginBottom: '0.5rem' }}>
            Can&rsquo;t find what you&rsquo;re looking for?
          </p>
          <p style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
            Contact your system administrator for additional support.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpPage;
